/**
 * Typed endpoint URL builders for the notifi monolith API.
 *
 * Each function returns the URL path (string) to pass to api client methods.
 * Path parameters are typed; query params are left to the caller via AxiosRequestConfig.
 *
 * Organized by domain — mirrors the categories in the original notifi-frontend Urls.js.
 */

// ─── Authentication ────────────────────────────────────────────────────────────

export const authEndpoints = {
  login: () => '/auth/login',
  refresh: () => '/auth/refresh',
  // The public forgot-password form has no tenant context, so these
  // endpoints accept calls without an `x-tenant-id` header and resolve
  // the tenant from the user (or, for reset, from the reset token).
  // PR #469 wired SendGrid delivery here.
  requestPasswordReset: () => '/auth/password/request-reset',
  validateResetToken: () => '/auth/password/reset/validate',
  resetPassword: () => '/auth/password/reset',
  inviteContext: (token: string) => `/auth/invites/${token}`,
  acceptInvite: (token: string) => `/auth/invites/${token}/accept`,
  impersonationEnd: () => '/auth/impersonate/end',
} as const;

// ─── Global Search ────────────────────────────────────────────────────────────

export const globalSearchEndpoints = {
  search: () => '/global-search',
} as const;

// ─── Contacts ─────────────────────────────────────────────────────────────────

export const contactEndpoints = {
  // ─── Operations service endpoints (no /api prefix) ──────────────────────
  list: () => '/contacts',
  search: () => '/contacts/search',
  detail: (id: string) => `/contacts/${id}`,
  summary: (id: string) => `/contacts/${id}/summary`,
  create: () => '/contacts',
  update: (id: string) => `/contacts/${id}`,
  delete: (id: string) => `/contacts/${id}`,
  restore: (id: string) => `/contacts/${id}/restore`,
  import: () => '/contacts/import',
  notes: (id: string) => `/contacts/${id}/notes`,
  note: (id: string, noteId: string) => `/contacts/${id}/notes/${noteId}`,
  addresses: (id: string) => `/contacts/${id}/addresses`,
  address: (id: string, addressId: string) => `/contacts/${id}/addresses/${addressId}`,
  setPrimaryAddress: (id: string, addressId: string) =>
    `/contacts/${id}/addresses/${addressId}/set-primary`,
  taxExemption: (id: string) => `/contacts/${id}/tax-exemption`,
  jobs: (id: string) => `/contacts/${id}/jobs`,
  estimates: (id: string) => `/contacts/${id}/estimates`,
  invoices: (id: string) => `/contacts/${id}/invoices`,
  history: (id: string) => `/contacts/${id}/history`,
  // NOT-573 — media roll-up across all the contact's jobs (and their visits).
  media: (id: string) => `/contacts/${id}/media`,
  attachments: (id: string) => `/contacts/${id}/attachments`,
  attachment: (id: string, attachmentId: string) => `/contacts/${id}/attachments/${attachmentId}`,
  attachmentUploadUrl: (id: string) => `/contacts/${id}/attachments/upload-url`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/contacts/${id}/attachments/${attachmentId}/download-url`,
  photos: (id: string) => `/contacts/${id}/photos`,
  photo: (id: string, photoId: string) => `/contacts/${id}/photos/${photoId}`,
  photoUploadUrl: (id: string) => `/contacts/${id}/photos/upload-url`,
  photoDownloadUrl: (id: string, photoId: string) =>
    `/contacts/${id}/photos/${photoId}/download-url`,
  tasks: (id: string) => `/contacts/${id}/tasks`,
  task: (taskId: string) => `/contact-tasks/${taskId}`,
  additionalContacts: (id: string) => `/contacts/${id}/additional-contacts`,
  additionalContact: (id: string, additionalContactId: string) =>
    `/contacts/${id}/additional-contacts/${additionalContactId}`,
  billTo: (id: string) => `/contacts/${id}/bill-to`,
  billToEntry: (id: string, billToId: string) => `/contacts/${id}/bill-to/${billToId}`,
  communications: (id: string) => `/contacts/${id}/communications`,
  paymentMethods: (id: string) => `/contacts/${id}/payment-methods`,
  paymentMethod: (id: string, paymentMethodId: string) =>
    `/contacts/${id}/payment-methods/${paymentMethodId}`,
  memberships: (id: string) => `/contacts/${id}/memberships`,
  activeMembership: (id: string) => `/contacts/${id}/memberships/active`,
  membership: (id: string, membershipId: string) => `/contacts/${id}/memberships/${membershipId}`,
  membershipBillingHistory: (id: string, membershipId: string) =>
    `/contacts/${id}/memberships/${membershipId}/billing-history`,
  membershipOpportunity: (id: string) => `/contacts/${id}/memberships/opportunity`,
  paymentMethodSetupIntent: (id: string) => `/contacts/${id}/payment-methods/setup-intent`,
  paymentMethodTokenize: (id: string) => `/contacts/${id}/payment-methods/tokenize`,
  paymentMethodRequest: (id: string) => `/contacts/${id}/payment-methods/request`,
  collectPayment: (id: string) => `/contacts/${id}/payments/collect`,
  requestPaymentLink: (id: string) => `/contacts/${id}/payments/request-link`,
  status: (id: string) => `/contacts/${id}/status`,
  createStripeSetupIntent: (id: string) => `/contacts/${id}/payment-methods/stripe/setup-intent`,
  syncStripePaymentMethods: (id: string) => `/contacts/${id}/payment-methods/stripe/sync`,
  setDefaultPaymentMethod: (id: string, paymentMethodId: string) =>
    `/contacts/${id}/payment-methods/${paymentMethodId}/default`,
  removePaymentMethod: (id: string, paymentMethodId: string) =>
    `/contacts/${id}/payment-methods/${paymentMethodId}`,
} as const;

// ─── Contact Statuses (operations) ──────────────────────────────────────────

export const contactStatusEndpoints = {
  list: () => '/contact-statuses',
  detail: (id: string) => `/contact-statuses/${id}`,
  reorder: () => '/contact-statuses/reorder',
} as const;

// ─── Leads ────────────────────────────────────────────────────────────────────

export const leadEndpoints = {
  list: () => '/leads',
  pipeline: () => '/leads/pipeline',
  detail: (id: string) => `/leads/${id}`,
  create: () => '/leads',
  update: (id: string) => `/leads/${id}`,
  delete: (id: string) => `/leads/${id}`,
  assign: (id: string) => `/leads/${id}/assign`,
  bulkAssign: () => '/leads/bulk-assign',
  autoAssign: (id: string) => `/leads/${id}/auto-assign`,
  bulkAutoAssign: () => '/leads/bulk-auto-assign',
  assignableUsers: () => '/leads/assignable-users',
  assignableTeams: () => '/leads/assignable-teams',
  assignableOwners: () => '/leads/assignable-owners',
  savedViews: () => '/leads/saved-views',
  savedView: (id: string) => `/leads/saved-views/${id}`,
  merge: (id: string) => `/leads/${id}/merge`,
  close: (id: string) => `/leads/${id}/close`,
  activities: (id: string) => `/leads/${id}/activities`,
  stages: () => '/lead-stages',
  stagesReorder: () => '/lead-stages/reorder',
  stageDetail: (id: string) => `/lead-stages/${id}`,
  convertToEstimate: (id: string) => `/leads/${id}/convert/estimate`,
  convertToJob: (id: string) => `/leads/${id}/convert/job`,
  settings: () => '/lead-settings',
} as const;

// ─── Acquisition Channels (operations — where work came from) ────────────────

export const acquisitionChannelEndpoints = {
  list: () => '/acquisition-channels',
  detail: (id: string) => `/acquisition-channels/${id}`,
} as const;

// ─── Lead Rep Availability (operations — assignment engine) ──────────────────

export const leadRepAvailabilityEndpoints = {
  list: () => '/lead-rep-availability',
  update: (userId: string) => `/lead-rep-availability/${userId}`,
  schedule: (userId: string) => `/lead-rep-availability/${userId}/schedule`,
} as const;

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const invoiceEndpoints = {
  list: () => '/invoices',
  listByJob: (jobId: string) => `/jobs/${jobId}/invoices`,
  createPreview: (jobId: string) => `/jobs/${jobId}/invoices/create-preview`,
  detail: (id: string) => `/invoices/${id}`,
  // Resolve the block layout this invoice renders through (null = classic).
  // previewDesign: a design uuid or 'default' to preview an unsaved pick.
  documentLayout: (id: string, previewDesign?: string | null) =>
    `/invoices/${id}/document-layout${
      previewDesign ? `?previewDesign=${encodeURIComponent(previewDesign)}` : ''
    }`,
  create: (jobId: string) => `/jobs/${jobId}/invoices`,
  update: (id: string) => `/invoices/${id}`,
  delete: (id: string) => `/invoices/${id}`,
  send: (id: string) => `/invoices/${id}/send`,
  sendEmail: (id: string) => `/invoices/${id}/send-email`,
  sendSms: (id: string) => `/invoices/${id}/send-sms`,
  remind: (id: string) => `/invoices/${id}/remind`,
  applyLateFee: (id: string) => `/invoices/${id}/apply-late-fee`,
  gatewayPayment: (id: string) => `/invoices/${id}/payments/gateway`,
  /** Ask the processor whether a settling bank payment has landed yet. */
  refreshPaymentSettlement: (paymentId: string) => `/payments/${paymentId}/refresh-settlement`,
  cancelBankPayment: (paymentId: string) => `/payments/${paymentId}/cancel-bank-payment`,
  void: (id: string) => `/invoices/${id}/void`,
  writeOff: (id: string) => `/invoices/${id}/write-off`,
  generatePaymentLink: (id: string) => `/invoices/${id}/payment-link`,
  attachmentUploadUrl: (id: string) => `/invoices/${id}/attachments/upload-url`,
  attachments: (id: string) => `/invoices/${id}/attachments`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/invoices/${id}/attachments/${attachmentId}`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/invoices/${id}/attachments/${attachmentId}/download-url`,
} as const;

/**
 * Payabli check scanning (remote deposit capture). The browser component reads
 * the check and debits it; these routes open the row before the debit and
 * settle it from Payabli's ledger after.
 */
export const chequeScanEndpoints = {
  availability: () => '/payments/cheque-scan/availability',
  claimForInvoice: (invoiceId: string) => `/payments/invoices/${invoiceId}/cheque-scan`,
  claimForEstimateDeposit: (estimateId: string) => `/estimates/${estimateId}/deposit/cheque-scan`,
  claimForJobDeposit: (jobId: string) => `/jobs/${jobId}/deposit/cheque-scan`,
  confirm: (paymentId: string) => `/payments/${paymentId}/cheque-scan/confirm`,
  fail: (paymentId: string) => `/payments/${paymentId}/cheque-scan/fail`,
} as const;

export const financingEndpoints = {
  detail: (entityType: 'estimate' | 'job' | 'invoice', entityId: string) =>
    `/financing/${entityType}/${entityId}`,
  recordPayment: (financingId: string) => `/financing/${financingId}/record-payment`,
  applyInvoicePayment: (invoiceId: string) => `/financing/invoice/${invoiceId}/apply-payment`,
} as const;

// ─── Tracking pixels (Wave 2 — Spec §3.2) ────────────────────────────────────

/**
 * Public endpoints embedded in outbound emails / customer-portal pages.
 *
 * `invoiceOpen(id)` returns a 1×1 transparent GIF and stamps
 * `Invoice.viewedAt` exactly once on the first hit. The endpoint is
 * `@Public()` on the operations service, so callers should never attach
 * the auth header.
 *
 * We resolve the host via the same `OPERATIONS_PUBLIC_URL` /
 * `PUBLIC_API_URL` env that the backend uses to render `<img src>` in
 * outbound emails — but for a same-origin SPA hit (the in-app preview)
 * a relative path works and is preferred to avoid CORS surprises.
 */
export const trackingEndpoints = {
  invoiceOpen: (id: string) => `/track/invoices/${id}/open.gif`,
} as const;

// ─── Notes (entity-scoped, operations service) ───────────────────────────────

export const noteEndpoints = {
  entity: (entityType: string, entityId: string) => `/${entityType}/${entityId}/notes`,
  detail: (id: string) => `/notes/${id}`,
  attachments: (id: string) => `/notes/${id}/attachments`,
  attachmentUploadUrl: (id: string) => `/notes/${id}/attachments/upload-url`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/notes/${id}/attachments/${attachmentId}/download-url`,
  attachmentDelete: (id: string, attachmentId: string) =>
    `/notes/${id}/attachments/${attachmentId}`,
} as const;

// ─── Change Orders ───────────────────────────────────────────────────────────

export const changeOrderEndpoints = {
  listByJob: (jobId: string) => `/jobs/${jobId}/change-orders`,
  create: (jobId: string) => `/jobs/${jobId}/change-orders`,
  preview: (jobId: string) => `/jobs/${jobId}/change-orders/preview`,
  scopeHistory: (jobId: string) => `/jobs/${jobId}/scope-history`,
  void: (jobId: string, id: string) => `/jobs/${jobId}/change-orders/${id}/void`,
  detail: (id: string) => `/change-orders/${id}`,
  update: (id: string) => `/change-orders/${id}`,
  submit: (id: string) => `/change-orders/${id}/submit`,
  send: (id: string) => `/change-orders/${id}/send`,
  approve: (id: string) => `/change-orders/${id}/approve`,
  reject: (id: string) => `/change-orders/${id}/reject`,
  apply: (id: string) => `/change-orders/${id}/apply`,
  // Public (customer-facing) endpoints — no auth, tenant via x-tenant-id.
  publicDetail: (id: string) => `/change-orders/public/${id}`,
  publicApprove: (id: string) => `/change-orders/public/${id}/customer-approve`,
  publicReject: (id: string) => `/change-orders/public/${id}/reject`,
} as const;

// ─── Progress Payments ──────────────────────────────────────────────────────

export const progressPaymentEndpoints = {
  listByJob: (jobId: string) => `/jobs/${jobId}/progress-payments`,
  invoice: (id: string) => `/progress-payments/${id}/invoice`,
} as const;

// ─── Projects (SPEC-projects) ───────────────────────────────────────────────

export const projectEndpoints = {
  list: () => '/projects',
  detail: (id: string) => `/projects/${id}`,
  summary: (id: string) => `/projects/${id}/summary`,
  entities: (id: string) => `/projects/${id}/entities`,
  fromJob: (jobId: string) => `/projects/from-job/${jobId}`,
  jobs: (id: string) => `/projects/${id}/jobs`,
  detachJob: (id: string, jobId: string) => `/projects/${id}/jobs/${jobId}`,
  dependencies: (id: string) => `/projects/${id}/dependencies`,
  dependency: (id: string, depId: string) => `/projects/${id}/dependencies/${depId}`,
  contract: (id: string) => `/projects/${id}/contract`,
  sovLines: (id: string) => `/projects/${id}/contract/sov-lines`,
  payApplications: (id: string) => `/projects/${id}/pay-applications`,
  payApplication: (appId: string) => `/pay-applications/${appId}`,
  payApplicationLines: (appId: string) => `/pay-applications/${appId}/lines`,
  payApplicationSubmit: (appId: string) => `/pay-applications/${appId}/submit`,
  payApplicationApprove: (appId: string) => `/pay-applications/${appId}/approve`,
  payApplicationInvoice: (appId: string) => `/pay-applications/${appId}/invoice`,
  payApplicationVoid: (appId: string) => `/pay-applications/${appId}/void`,
  payApplicationNotarized: (appId: string) => `/pay-applications/${appId}/notarized`,
  payApplicationPdf: (appId: string) => `/pay-applications/${appId}/pdf`,
  lienWaivers: (id: string) => `/projects/${id}/lien-waivers`,
  lienWaiver: (waiverId: string) => `/lien-waivers/${waiverId}`,
  attachableEstimates: (id: string) => `/projects/${id}/attachable-estimates`,
  estimates: (id: string) => `/projects/${id}/estimates`,
  detachEstimate: (id: string, estimateId: string) => `/projects/${id}/estimates/${estimateId}`,
  attachments: (id: string) => `/projects/${id}/attachments`,
  attachmentUploadUrl: (id: string) => `/projects/${id}/attachments/upload-url`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/projects/${id}/attachments/${attachmentId}/download-url`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/projects/${id}/attachments/${attachmentId}`,
  payApplicationNotarizedUploadUrl: (appId: string) =>
    `/pay-applications/${appId}/notarized-upload-url`,
  payApplicationNotarizedDownloadUrl: (appId: string) =>
    `/pay-applications/${appId}/notarized/download-url`,
} as const;

// ─── Pricebook / Line Items ───────────────────────────────────────────────────

/**
 * Operations microservice pricebook endpoints.
 * Operations pricebook endpoints — same paths as monolith REST,
 * different base URL (operationsClient resolves to VITE_OPERATIONS_URL).
 */
/** The companies we buy from. */
export const supplierEndpoints = {
  list: () => '/suppliers',
  detail: (id: string) => `/suppliers/${id}`,
  create: () => '/suppliers',
  update: (id: string) => `/suppliers/${id}`,
  contacts: (id: string) => `/suppliers/${id}/contacts`,
  contact: (id: string, contactId: string) => `/suppliers/${id}/contacts/${contactId}`,
  branches: (id: string) => `/suppliers/${id}/branches`,
  branch: (id: string, branchId: string) => `/suppliers/${id}/branches/${branchId}`,
};

export const purchasingSettingsEndpoints = {
  get: () => '/settings/purchasing',
  update: () => '/settings/purchasing',
};

/** Purchasing in the accounting system (PUR-ACC). */
export const purchasingAccountingEndpoints = {
  vendorLinks: () => '/purchasing/accounting/vendor-links',
  journals: () => '/purchasing/accounting/journals',
  journal: (id: string) => `/purchasing/accounting/journals/${id}`,
  buildJournals: () => '/purchasing/accounting/journals/build',
  postJournal: (id: string) => `/purchasing/accounting/journals/${id}/post`,
  refreshLockDate: () => '/purchasing/accounting/lock-date/refresh',
  usage: () => '/settings/accounting/usage',
};

export const operationsPricebookEndpoints = {
  list: () => '/pricebook/line-items',
  detail: (id: string) => `/pricebook/line-items/${id}`,
  usage: (id: string) => `/pricebook/line-items/${id}/usage`,
  create: () => '/pricebook/line-items',
  update: (id: string) => `/pricebook/line-items/${id}`,
  /**
   * NOT-105: there is no hard-delete endpoint. The server's `DELETE`
   * route is a soft-archive (status → ARCHIVED, isDeleted = true).
   * `archive` is the canonical name; `delete` is kept as a deprecated
   * alias so existing call sites continue to compile while we migrate.
   */
  archive: (id: string) => `/pricebook/line-items/${id}`,
  restore: (id: string) => `/pricebook/line-items/${id}/restore`,
  /** @deprecated NOT-105: hard delete is not supported. Use `archive`. */
  delete: (id: string) => `/pricebook/line-items/${id}`,
  import: () => '/pricebook/line-items/bulk-import',
  uploadImage: (id: string) => `/pricebook/line-items/${id}/image`,
  imageUploadUrl: (id: string) => `/pricebook/line-items/${id}/image-upload-url`,
  /** NOT-590 — the materials/equipment a SERVICE includes. */
  components: (id: string) => `/pricebook/line-items/${id}/components`,
  /** Who supplies this item, and at what cost. MATERIAL/EQUIPMENT only. */
  suppliers: (id: string) => `/pricebook/line-items/${id}/suppliers`,
  itemSupplier: (id: string, itemSupplierId: string) =>
    `/pricebook/line-items/${id}/suppliers/${itemSupplierId}`,
  itemSupplierPrimary: (id: string, itemSupplierId: string) =>
    `/pricebook/line-items/${id}/suppliers/${itemSupplierId}/primary`,
  /** Every cost set or seen from each supplier (PUR-CAT-6). */
  itemCostHistory: (id: string) => `/pricebook/line-items/${id}/suppliers/cost-history`,
  /** What to order once suppliers stop selling the item (PUR-CAT-12). */
  itemReplacement: (id: string) => `/pricebook/line-items/${id}/suppliers/replacement`,
  // ── Categories (NOT-590) ──────────────────────────────────────────────
  categoryList: () => '/pricebook/categories',
  categoryTree: () => '/pricebook/categories/tree',
  categoryCreate: () => '/pricebook/categories',
  categoryUpdate: (id: string) => `/pricebook/categories/${id}`,
  categoryDelete: (id: string) => `/pricebook/categories/${id}`,
  /** Owner/admin only — business-unit / role / mobile BROWSE visibility. */
  categoryScope: (id: string) => `/pricebook/categories/${id}/scope`,
  categoryMove: (id: string) => `/pricebook/categories/${id}/move`,
  categoryReorder: () => '/pricebook/categories/reorder',
  categoryItems: (id: string) => `/pricebook/categories/${id}/items`,
  categoryItemsRemove: (id: string) => `/pricebook/categories/${id}/items/remove`,
  categoryItemsOrder: (id: string) => `/pricebook/categories/${id}/items/order`,
  categoryBulkEdit: (id: string) => `/pricebook/categories/${id}/bulk-edit`,
  /** An item's category memberships + its single primary. */
  itemCategories: (lineItemId: string) => `/pricebook/categories/items/${lineItemId}`,
  // Membership plans
  membershipPlans: () => '/pricebook/membership-plans',
  membershipPricingContext: () => '/pricebook/membership-plans/pricing-context',
  membershipPlan: (id: string) => `/pricebook/membership-plans/${id}`,
  restoreMembershipPlan: (id: string) => `/pricebook/membership-plans/${id}/restore`,
  membershipPlanStats: (id: string) => `/pricebook/membership-plans/${id}/stats`,
  membershipPlanEconomicsPreview: () => '/pricebook/membership-plans/economics/preview',
  syncMembershipPlanStripe: (id: string) => `/pricebook/membership-plans/${id}/stripe/sync`,
  planRecurringServices: (planId: string) =>
    `/pricebook/membership-plans/${planId}/recurring-services`,
  planRecurringService: (planId: string, id: string) =>
    `/pricebook/membership-plans/${planId}/recurring-services/${id}`,
} as const;

// ─── Technician time off (operations service) ───────────────────────────────

export const timeOffEndpoints = {
  list: (technicianId: string) => `/technicians/${technicianId}/time-off`,
  create: (technicianId: string) => `/technicians/${technicianId}/time-off`,
  approve: (id: string) => `/time-off/${id}/approve`,
  decline: (id: string) => `/time-off/${id}/decline`,
  cancel: (id: string) => `/time-off/${id}/cancel`,
} as const;

// ─── Payroll profiles (operations service) ──────────────────────────────────

export const payrollProfileEndpoints = {
  list: () => '/payroll-profiles',
  detail: (id: string) => `/payroll-profiles/${id}`,
} as const;

// ─── Memberships ─────────────────────────────────────────────────────────────

// NOT-339 §3.3 — the standalone Contract aggregate + its `/contracts/*`
// endpoints are retired. Recurring agreements are Jobs; recurring lifecycle
// lives on `recurringJobEndpoints` (/v2/jobs/*) and the recurring config is
// written via `jobEndpoints.update`.

export const membershipEndpoints = {
  list: () => '/memberships',
  detail: (id: string) => `/memberships/${id}`,
  create: () => '/memberships',
  update: (id: string) => `/memberships/${id}`,
  activate: (id: string) => `/memberships/${id}/activate`,
  repairIncludedVisits: (id: string) => `/memberships/${id}/repair-included-visits`,
  prepaidInvoice: (id: string) => `/memberships/${id}/prepaid-invoice`,
  cancel: (id: string) => `/memberships/${id}/cancel`,
  cancellationPreview: (id: string) => `/memberships/${id}/cancellation-preview`,
  suspend: (id: string) => `/memberships/${id}/suspend`,
  reactivate: (id: string) => `/memberships/${id}/reactivate`,
  renew: (id: string) => `/memberships/${id}/renew`,
  renewContract: (id: string) => `/memberships/${id}/renew-contract`,
  retryPayment: (id: string) => `/memberships/${id}/retry-payment`,
  changePlan: (id: string) => `/memberships/${id}/change-plan`,
  saveOffer: (id: string) => `/memberships/${id}/save-offer`,
  // Reports
  reportActivity: (params?: ReportPeriodParams) =>
    `/reports/membership/activity${reportPeriodQuery(params)}`,
  reportOverview: () => '/reports/membership/overview',
  reportRevenue: () => '/reports/membership/revenue',
  reportChurn: () => '/reports/membership/churn',
  reportSales: () => '/reports/membership/sales',
  reportUtilization: () => '/reports/membership/utilization',
  reportPlanPerformance: () => '/reports/membership/plan-performance',
  // Selling
  estimateSavings: (estimateId: string) => `/estimates/${estimateId}/membership-savings`,
  jobOpportunity: (jobId: string) => `/jobs/${jobId}/membership-opportunity`,
  // Recurring service events (included-visit booking queue)
  recurringServiceEvents: (status?: string) =>
    `/recurring-service-events${status ? `?status=${status}` : ''}`,
  // Included visits — one row per active recurring service (entitlement view)
  recurringVisitEntitlements: (opts?: { cursor?: string; limit?: number }) => {
    const p = new URLSearchParams();
    if (opts?.cursor) p.set('cursor', opts.cursor);
    if (opts?.limit) p.set('limit', String(opts.limit));
    const q = p.toString();
    return `/recurring-service-events/entitlements${q ? `?${q}` : ''}`;
  },
  bookRecurringServiceEvent: (id: string) => `/recurring-service-events/${id}/book`,
  // Several due visits at one home, booked into ONE job (one trip).
  bookRecurringServiceEvents: () => '/recurring-service-events/book',
  // A due visit added to a job that already exists at the same home.
  attachRecurringServiceEvent: (id: string) => `/recurring-service-events/${id}/attach`,
  // An added visit taken back off its job — back to the queue.
  detachRecurringServiceEvent: (id: string) => `/recurring-service-events/${id}/detach`,
  // The Recurring Service Events queue (ServiceTitan parity) — flat, paged,
  // filterable list of pending visits to book.
  recurringServiceQueue: (params?: {
    page?: number;
    limit?: number;
    customerId?: string;
    technicianId?: string;
    businessUnitId?: string;
    availability?: 'bookable' | 'not_bookable';
    outreach?: 'needs_office';
    search?: string;
    sortDir?: 'asc' | 'desc';
  }) => {
    const p = new URLSearchParams();
    if (params?.page) p.set('page', String(params.page));
    if (params?.limit) p.set('limit', String(params.limit));
    if (params?.customerId) p.set('customerId', params.customerId);
    if (params?.technicianId) p.set('technicianId', params.technicianId);
    if (params?.businessUnitId) p.set('businessUnitId', params.businessUnitId);
    if (params?.availability) p.set('availability', params.availability);
    if (params?.outreach) p.set('outreach', params.outreach);
    if (params?.search) p.set('search', params.search);
    if (params?.sortDir) p.set('sortDir', params.sortDir);
    const q = p.toString();
    return `/recurring-service-events/queue${q ? `?${q}` : ''}`;
  },
  dismissRecurringServiceEvent: (id: string) => `/recurring-service-events/${id}/dismiss`,
  bulkDismissRecurringServiceEvents: () => '/recurring-service-events/dismiss',
  // Per-membership recurring service instances (edit the customer's own copy)
  recurringServices: (membershipId: string) => `/memberships/${membershipId}/recurring-services`,
  recurringService: (membershipId: string, id: string) =>
    `/memberships/${membershipId}/recurring-services/${id}`,
} as const;

// ─── Webhook Subscriptions (operations) ──────────────────────────────────────

export const webhookSubscriptionEndpoints = {
  list: () => '/webhook-subscriptions',
  events: () => '/webhook-subscriptions/events',
  detail: (id: string) => `/webhook-subscriptions/${id}`,
  deliveries: (id: string, params?: { page?: number; limit?: number }) => {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return `/webhook-subscriptions/${id}/deliveries${query ? `?${query}` : ''}`;
  },
  create: () => '/webhook-subscriptions',
  update: (id: string) => `/webhook-subscriptions/${id}`,
  setEnabled: (id: string) => `/webhook-subscriptions/${id}/enabled`,
  test: (id: string) => `/webhook-subscriptions/${id}/test`,
  rotateSecret: (id: string) => `/webhook-subscriptions/${id}/rotate-secret`,
  retryDelivery: (id: string, deliveryId: string) =>
    `/webhook-subscriptions/${id}/deliveries/${deliveryId}/retry`,
  delete: (id: string) => `/webhook-subscriptions/${id}`,
} as const;

export const partnerWebhookSubscriptionEndpoints = {
  list: () => '/settings/partner-webhooks',
  events: () => '/settings/partner-webhooks/events',
  detail: (id: string) => `/settings/partner-webhooks/${id}`,
  deliveries: (id: string, params?: { page?: number; limit?: number }) => {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return `/settings/partner-webhooks/${id}/deliveries${query ? `?${query}` : ''}`;
  },
  create: () => '/settings/partner-webhooks',
  update: (id: string) => `/settings/partner-webhooks/${id}`,
  setEnabled: (id: string) => `/settings/partner-webhooks/${id}/enabled`,
  test: (id: string) => `/settings/partner-webhooks/${id}/test`,
  rotateSecret: (id: string) => `/settings/partner-webhooks/${id}/rotate-secret`,
  retryDelivery: (id: string, deliveryId: string) =>
    `/settings/partner-webhooks/${id}/deliveries/${deliveryId}/retry`,
  delete: (id: string) => `/settings/partner-webhooks/${id}`,
} as const;

export type WebhookSubscriptionSurface = 'generic' | 'partner';

export function endpointsForWebhookSurface(surface: WebhookSubscriptionSurface) {
  return surface === 'partner' ? partnerWebhookSubscriptionEndpoints : webhookSubscriptionEndpoints;
}

// ─── Zapier / OAuth connections ───────────────────────────────────────────────
//
// Zapier connects to Notifi via the operations OAuth provider. The consent /
// token exchange happens inside Zapier; these endpoints let the tenant view and
// revoke the resulting OAuth connections from the Connected Apps page.

export const zapierEndpoints = {
  connections: () => '/oauth/connections',
  revokeConnection: (id: string) => `/oauth/connections/${id}/revoke`,
  connectionChannel: (id: string) => `/oauth/connections/${id}/channel`,
  // Per-environment Zapier "Connect" deep-link, served by the backend so the
  // card's visibility is driven by runtime config (no frontend rebuild).
  config: () => '/oauth/zapier/config',
} as const;

// ─── Settings (new REST) ──────────────────────────────────────────────────────

export const emailChannelEndpoints = {
  // Per-user connected email mailboxes (Gmail/Outlook).
  accounts: () => '/messaging/email/accounts',
  oauthUrl: (provider: 'gmail' | 'outlook') => `/messaging/email/oauth/${provider}/url`,
  disconnect: (id: string) => `/messaging/email/accounts/${id}`,
  updateSignature: (id: string) => `/messaging/email/accounts/${id}/signature`,
  // Per-tenant inbound ignore rules.
  ignoreRules: () => '/messaging/email/ignore-rules',
  ignoreRule: (id: string) => `/messaging/email/ignore-rules/${encodeURIComponent(id)}`,
  blockSender: () => '/messaging/email/ignore-rules/block-sender',
};

export const settingsEndpoints = {
  company: () => '/settings/company',
  updateCompany: () => '/settings/company',
  uploadLogoPresign: () => '/settings/company/logo/presign',
  uploadLogo: () => '/settings/company/logo',
  // Scheduler "business settings" (slot duration, buffers, travel-time, etc.).
  // Distinct from /settings/company — these are the knobs the scheduling engine
  // reads when computing availability and travel-aware conflicts.
  businessSettings: () => '/settings',
  /** PLAT-FF-1 — every rollout feature flag, evaluated for the tenant. */
  featureFlags: () => '/feature-flags',
  updateBusinessSettings: () => '/settings',
  estimateSettings: () => '/settings/estimate',
  wisetackSettings: () => '/settings/wisetack',
  wisetackSimulateStatus: () => '/settings/wisetack/simulate-status',
  // Client portal enablement (the gate the customer portal auth boundary reads).
  clientPortal: () => '/settings/client-portal',
  updateClientPortal: () => '/settings/client-portal',
  // `docType` scopes the switch to a document family (INVOICE toggles
  // independently of estimates); omitted ⇒ estimate/proposal.
  estimateDocTemplates: (docType?: string) =>
    `/settings/estimate-doc-templates${docType ? `?docType=${encodeURIComponent(docType)}` : ''}`,
  updateEstimateDocTemplates: (docType?: string) =>
    `/settings/estimate-doc-templates${docType ? `?docType=${encodeURIComponent(docType)}` : ''}`,
  // Terms Templates
  termsTemplates: () => '/settings/terms-templates',
  termsTemplateDetail: (id: string) => `/settings/terms-templates/${id}`,
  termsTemplateDefault: (id: string) => `/settings/terms-templates/${id}/default`,
  // Scope of Work Templates
  scopeOfWorkTemplates: () => '/settings/scope-of-work-templates',
  scopeOfWorkTemplateDetail: (id: string) => `/settings/scope-of-work-templates/${id}`,
  scopeOfWorkTemplateDefault: (id: string) => `/settings/scope-of-work-templates/${id}/default`,
  // Users
  users: () => '/users',
  createUser: () => '/users',
  // NOT-509: salesperson/owner picker candidates — members with estimates:create.
  salesAttributionCandidates: (includeUserId?: string) =>
    includeUserId
      ? `/users/sales-attribution-candidates?includeUserId=${encodeURIComponent(includeUserId)}`
      : '/users/sales-attribution-candidates',
  // Self-lookup — does NOT require the `users:view` permission, so it works
  // for ordinary (non-admin) users including impersonated sessions whose
  // underlying user lacks that permission.
  userMe: () => '/users/me',
  userMeEmailSignature: () => '/users/me/email-signature',
  userMeScheduleDisplayTimezone: () => '/users/me/schedule-display-timezone',
  userDetail: (userId: string) => `/users/${userId}`,
  updateUser: (userId: string) => `/users/${userId}`,
  deleteUser: (userId: string) => `/users/${userId}`,
  assignUserRoles: (userId: string) => `/users/${userId}/roles`,
  removeUserRole: (userId: string, roleId: string) => `/users/${userId}/roles/${roleId}`,
  resendInvitation: (userId: string) => `/users/${userId}/resend-invitation`,
  cancelInvitation: (userId: string) => `/users/${userId}/cancel-invitation`,
  userGroups: (id: string) => '/users/' + id + '/groups',
  // Invites (auth service)
  inviteUser: () => '/auth/invites',
  // Roles
  roles: () => '/roles',
  roleDetail: (roleId: string) => `/roles/${roleId}`,
  roleClone: (roleId: string) => `/roles/${roleId}/clone`,
  // Permissions
  permissionCatalog: () => '/permissions/catalog',
  permissionsMe: () => '/permissions/me',
  notificationPreferences: () => '/settings/notifications',
  resetNotificationPreferences: () => '/settings/notifications/reset',
  operationalNotificationRules: () => '/settings/notifications/operational-rules',
  // Groups
  groups: () => '/groups',
  groupDetail: (groupId: string) => `/groups/${groupId}`,
  groupMembers: (groupId: string) => `/groups/${groupId}/members`,
  groupMember: (groupId: string, membershipId: string) =>
    `/groups/${groupId}/members/${membershipId}`,
  // QuickBooks OAuth (provider-specific, stays at /settings/quickbooks/)
  qbAuthUrl: () => '/settings/quickbooks/auth-url',
  qbDisconnect: () => '/settings/quickbooks/disconnect',
  // Accounting sync settings (provider-agnostic — resolves QB, Xero, NetSuite)
  qbStatus: () => '/settings/accounting/status',
  qbFieldMapping: () => '/settings/accounting/field-mapping',
  qbSync: () => '/settings/accounting/sync',
  qbSyncPreview: () => '/settings/accounting/sync-preview',
  qbSyncSelected: () => '/settings/accounting/sync-selected',
  qbSyncLog: () => '/settings/accounting/sync-log',
  // Tenant accounting configuration (Configuration tab — spec §16.2)
  qbAccountConfig: () => '/settings/quickbooks/account-config',
  qbAccounts: () => '/settings/quickbooks/accounts',
  qbClasses: () => '/settings/quickbooks/classes',
  qbPaymentMethods: () => '/settings/quickbooks/payment-methods',
  qbPullContact: (contactId: string) => `/settings/quickbooks/contacts/${contactId}/pull`,
  qbEntityPushPreview: (entity: string) => `/settings/quickbooks/sync-preview/push/${entity}`,
  qbEntityPullPreview: (entity: string) => `/settings/quickbooks/sync-preview/pull/${entity}`,
  // Accounting sync dashboard / failures / drill-down (spec §15, §17.3)
  accountingSyncDashboard: () => '/settings/accounting/sync/dashboard',
  // NOT-596 — deferred revenue recognition report
  revenueRecognitionSummary: () => '/accounting/revenue-recognition/summary',
  revenueRecognitionSchedules: () => '/accounting/revenue-recognition/schedules',
  accountingSyncHistory: () => '/settings/accounting/sync/history',
  accountingSyncFailures: () => '/settings/accounting/sync/failures',
  accountingSyncRetry: (logId: string) => `/settings/accounting/sync/retry/${logId}`,
  accountingSyncSkip: (logId: string) => `/settings/accounting/sync/skip/${logId}`,
  accountingSyncForceEntity: (type: string, id: string) =>
    `/settings/accounting/sync/entity/${type}/${id}`,
  accountingSyncEntityStatus: (type: string, id: string) =>
    `/settings/accounting/sync/entity/${type}/${id}`,
  accountingSyncExclusions: () => '/settings/accounting/sync/exclusions',
  accountingSyncRemoveExclusion: (type: string, id: string) =>
    `/settings/accounting/sync/exclusions/${type}/${id}`,
  accountingSyncConflicts: () => '/settings/accounting/sync/conflicts',
  accountingSyncConflictDiff: (logId: string) =>
    `/settings/accounting/sync/conflicts/${logId}/diff`,
  accountingSyncResolveConflict: (logId: string) =>
    `/settings/accounting/sync/conflicts/${logId}/resolve`,
  // Job types
  jobTypes: () => '/job-types',
  jobType: (id: string) => `/job-types/${id}`,
  // Online booking config
  bookableServices: () => '/bookable-services',
  bookableService: (id: string) => `/bookable-services/${id}`,
  bookingLinks: () => '/booking-links',
  bookingLink: (id: string) => `/booking-links/${id}`,
  /** Copy-paste embed snippet for a booking link's widget. */
  bookingLinkEmbed: (id: string) => `/booking-links/${id}/embed`,
  // Managed short links (the "Short Links" settings page)
  shortLinks: () => '/short-links',
  shortLink: (id: string) => `/short-links/${id}`,
  revokeShortLink: (id: string) => `/short-links/${id}/revoke`,
  // Partner API keys (the "API Keys" settings page)
  partnerApiKeys: () => '/settings/partner-api-keys',
  partnerApiKey: (id: string) => `/settings/partner-api-keys/${id}`,
  // Checklists — definitions
  checklistTemplates: () => '/checklist-definitions',
  checklistTemplate: (id: string) => `/checklist-definitions/${id}`,
  checklistTemplateArchive: (id: string) => `/checklist-definitions/${id}/archive`,
  checklistTemplateRestore: (id: string) => `/checklist-definitions/${id}/restore`,
  checklistTemplateDuplicate: (id: string) => `/checklist-definitions/${id}/duplicate`,
  checklistTemplateVersions: (id: string) => `/checklist-definitions/${id}/versions`,
  checklistTemplateJobTypes: (id: string) => `/checklist-definitions/${id}/job-types`,
  checklistTemplateJobType: (id: string, jobTypeId: string) =>
    `/checklist-definitions/${id}/job-types/${jobTypeId}`,
  // Checklists — instances (appointment-scoped)
  appointmentChecklists: (appointmentId: string) => `/appointments/${appointmentId}/checklists`,
  appointmentChecklistSnapshot: (appointmentId: string) =>
    `/appointments/${appointmentId}/checklists/snapshot`,
  appointmentChecklistSync: (appointmentId: string) =>
    `/appointments/${appointmentId}/checklists/sync`,
  appointmentChecklist: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}`,
  appointmentChecklistItem: (
    appointmentId: string,
    instanceId: string,
    sectionIdx: number,
    itemIdx: number,
  ) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/sections/${sectionIdx}/items/${itemIdx}`,
  appointmentChecklistItemPhotos: (
    appointmentId: string,
    instanceId: string,
    sectionIdx: number,
    itemIdx: number,
  ) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/sections/${sectionIdx}/items/${itemIdx}/photos`,
  appointmentChecklistItemPhoto: (
    appointmentId: string,
    instanceId: string,
    sectionIdx: number,
    itemIdx: number,
    photoId: string,
  ) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/sections/${sectionIdx}/items/${itemIdx}/photos/${photoId}`,
  appointmentChecklistRepeatSelections: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/repeat-selections`,
  appointmentChecklistVisibility: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/visibility`,
  appointmentChecklistComplete: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/complete`,
  appointmentChecklistReport: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/report`,
  appointmentChecklistReportUploadUrl: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/report/upload-url`,
  appointmentChecklistReportPdf: (appointmentId: string, instanceId: string) =>
    `/appointments/${appointmentId}/checklists/${instanceId}/report/pdf`,
  jobChecklistReports: (jobId: string) => `/jobs/${jobId}/checklist-reports`,
  // Payment settings
  paymentSettings: () => '/api/settings/payments',
  stripeConnect: () => '/api/settings/payments/stripe-connect',
  stripeAccountSession: () => '/api/settings/payments/stripe-account-session',
  payabliConnection: () => '/api/settings/payments/payabli-connect',
  payabliClientConfig: () => '/api/settings/payments/payabli-client-config',
} as const;

// ─── Billing Settings (PR 9 — spec §10, six-tab unified panel) ────────────────

/**
 * Path segment per tab. Backend exposes named handlers at the kebab-case
 * versions (`payment-methods`, `credit-memos`, `quickbooks`) and a generic
 * `:tab` catch-all that accepts the camelCase BillingSettingsTab keys, so
 * either form works. We use the named (kebab) form to match Swagger.
 */
const BILLING_TAB_PATHS = {
  invoices: 'invoices',
  receipts: 'receipts',
  deposits: 'deposits',
  paymentMethods: 'payment-methods',
  creditMemos: 'credit-memos',
  qbo: 'quickbooks',
} as const;

type BillingTabKey = keyof typeof BILLING_TAB_PATHS;

export const billingSettingsEndpoints = {
  /** GET full BillingSettings shape with defaults filled in. */
  get: () => '/settings/billing',
  /** PATCH a single tab. */
  updateTab: (tab: BillingTabKey) => `/settings/billing/${BILLING_TAB_PATHS[tab]}`,
} as const;

// ─── Timesheets ───────────────────────────────────────────────────────────────

// Operations service (no /api prefix). Aggregation + approval over real
// TimeEntry rows — replaces the retired System B ledger (/api/timesheet/*).
export const timesheetEndpoints = {
  period: () => '/timesheets',
  export: () => '/timesheets/export',
  dayEntries: () => '/timesheets/entries',
  approve: () => '/timesheets/approve',
  reopen: () => '/timesheets/reopen',
  entryDetail: (id: string) => `/time-entries/${id}`,
  createEntry: (appointmentId: string) => `/appointments/${appointmentId}/time-entries`,
  // Office-create with the appointment in the BODY (optional) — supports
  // OVERHEAD (no-appointment) entries the appointment-scoped route can't.
  createSelected: () => '/time-entries',
  // Operations calendar — appointments for the "Add entry" picker.
  dayAppointments: () => '/calendar/events',
  // Owner/admin override valve (timesheets:approve). The normal edit/create/
  // delete routes refuse once a job is invoiced or a pay period is approved;
  // these three carry a mandatory reason and are recorded in the audit trail.
  overrideEntry: (id: string) => `/time-entries/${id}/override`,
  overrideCreate: () => '/time-entries/override',
  // Who is on the clock right now, with each session's age and lock state.
  openSessions: () => '/timesheets/open-sessions',
  entryHistory: (id: string) => `/time-entries/${id}/history`,
} as const;

// ─── Appointment Forms (operations) ──────────────────────────────────────────

export const appointmentFormEndpoints = {
  // Form definitions
  definitions: () => '/appointment-forms',
  definition: (id: string) => `/appointment-forms/${id}`,
  // Submissions
  definitionsForAppointment: (appointmentId: string) =>
    `/appointments/${appointmentId}/form-definitions`,
  submissionsForAppointment: (appointmentId: string) =>
    `/appointments/${appointmentId}/form-submissions`,
  submission: (submissionId: string) => `/form-submissions/${submissionId}`,
} as const;

// ─── Unified Forms Engine ───────────────────────────────────────────────────

export const formsEngineEndpoints = {
  templates: () => '/forms/templates',
  template: (id: string) => `/forms/templates/${id}`,
  templateVersions: (id: string) => `/forms/templates/${id}/versions`,
  templateImpact: (id: string) => `/forms/templates/${id}/impact`,
  publishTemplate: (id: string) => `/forms/templates/${id}/publish`,
  saveTemplate: (id: string) => `/forms/templates/${id}/save`,
  templateVersion: (id: string, versionId: string) =>
    `/forms/templates/${id}/versions/${versionId}`,
  /** Publishes an earlier version again, as a new version. `forms:edit`. */
  restoreTemplateVersion: (id: string, versionId: string) =>
    `/forms/templates/${id}/versions/${versionId}/restore`,
  templateAssignments: (id: string) => `/forms/templates/${id}/assignments`,
  templateAssignment: (id: string, assignmentId: string) =>
    `/forms/templates/${id}/assignments/${assignmentId}`,
  templateLeadSource: (id: string) => `/forms/templates/${id}/lead-source`,
  questions: () => '/forms/questions',
  question: (id: string) => `/forms/questions/${id}`,
  questionVersions: (id: string) => `/forms/questions/${id}/versions`,
  questionVersion: (id: string, versionId: string) =>
    `/forms/questions/${id}/versions/${versionId}`,
  questionImpact: (id: string) => `/forms/questions/${id}/impact`,
  publishQuestion: (id: string) => `/forms/questions/${id}/publish`,
  archiveQuestion: (id: string) => `/forms/questions/${id}/archive`,
  restoreQuestion: (id: string) => `/forms/questions/${id}/restore`,
  sections: () => '/forms/sections',
  section: (id: string) => `/forms/sections/${id}`,
  sectionVersions: (id: string) => `/forms/sections/${id}/versions`,
  sectionVersion: (id: string, versionId: string) => `/forms/sections/${id}/versions/${versionId}`,
  sectionImpact: (id: string) => `/forms/sections/${id}/impact`,
  publishSection: (id: string) => `/forms/sections/${id}/publish`,
  archiveSection: (id: string) => `/forms/sections/${id}/archive`,
  restoreSection: (id: string) => `/forms/sections/${id}/restore`,
  sets: () => '/forms/sets',
  set: (id: string) => `/forms/sets/${id}`,
  setVersions: (id: string) => `/forms/sets/${id}/versions`,
  setVersion: (id: string, versionId: string) => `/forms/sets/${id}/versions/${versionId}`,
  publishSet: (id: string) => `/forms/sets/${id}/publish`,
  applySet: (id: string) => `/forms/sets/${id}/apply`,
  archiveSet: (id: string) => `/forms/sets/${id}/archive`,
  restoreSet: (id: string) => `/forms/sets/${id}/restore`,
  instances: () => '/forms/instances',
  instance: (id: string) => `/forms/instances/${id}`,
  instanceRepeats: (id: string) => `/forms/instances/${id}/repeats`,
  instanceItemMedia: (id: string, itemId: string) => `/forms/instances/${id}/items/${itemId}/media`,
  appointmentSnapshot: (appointmentId: string) => `/forms/appointments/${appointmentId}/snapshot`,
  appointmentSync: (appointmentId: string) => `/forms/appointments/${appointmentId}/sync`,
  publicLinks: (templateId: string) => `/forms/templates/${templateId}/public-links`,
  intakePublicLink: (templateId: string) => `/forms/templates/${templateId}/public-link`,
  regenerateIntakePublicLink: (templateId: string) =>
    `/forms/templates/${templateId}/public-link/regenerate`,
  /**
   * Public (unauthenticated) presign for a FILE_UPLOAD answer on a permanent
   * intake link. Legacy signed-token forms have no upload route.
   */
  publicIntakeUpload: (key: string) =>
    `/api/public/forms/intake/${encodeURIComponent(key)}/uploads`,
  /** A lead's own intake submissions (newest first), for its Form tab. `leads:view`. */
  intakeSubmissionsByLead: (leadId: string) => `/forms/intake/submissions/by-lead/${leadId}`,
  /**
   * Every submission of one intake form, newest first, for the builder's
   * Activity tab. `forms:view`. Pass `{ templateId, cursor?, limit? }` via
   * the request's `params`.
   */
  intakeSubmissions: () => '/forms/intake/submissions',
  /**
   * The builder's Activity tab (Task 11) — exceptions only (NEEDS_ATTENTION /
   * SPAM), plus `leads30d` for the same template. `forms:view`. Pass
   * `{ templateId }` (and optionally `status`) via the request's `params`.
   */
  intakeActivity: () => '/forms/intake/submissions/activity',
  /** One row per intake template with any activity, for the Forms library badges. `forms:view`. */
  intakeActivitySummary: () => '/forms/intake/submissions/summary',
  intakeSubmissionRetry: (id: string) => `/forms/intake/submissions/${id}/retry`,
  intakeSubmissionDismiss: (id: string) => `/forms/intake/submissions/${id}/dismiss`,
  intakeSubmissionMarkNotSpam: (id: string) => `/forms/intake/submissions/${id}/not-spam`,
} as const;

// ─── Reminder Policies (operations v2) ──────────────────────────────────────

export const reminderPoliciesV2 = {
  list: () => '/v2/reminder-policies',
  detail: (type: string) => `/v2/reminder-policies/${type}`,
  update: (type: string) => `/v2/reminder-policies/${type}`,
  reset: (type: string) => `/v2/reminder-policies/${type}/reset`,
} as const;

// ─── Reminders Queue (operations v2) ────────────────────────────────────────
//
// Backend controller is mounted at `/reminders` on the operations service.

export const remindersV2 = {
  list: () => '/reminders',
  detail: (id: string) => `/reminders/${id}`,
  previewNextStep: (id: string) => `/reminders/${id}/preview-next-step`,
  editStep: (id: string, stepId: string) => `/reminders/${id}/steps/${stepId}`,
  skipNextStep: (id: string) => `/reminders/${id}/skip-next-step`,
  sendNow: (id: string) => `/reminders/${id}/send-now`,
  pause: (id: string) => `/reminders/${id}/pause`,
  resume: (id: string) => `/reminders/${id}/resume`,
  cancel: (id: string) => `/reminders/${id}/cancel`,
  auditLog: (id: string) => `/reminders/${id}/audit-log`,
} as const;

// ─── Reviews v2 (operations service: /v2/reviews) ────────────────────────────
//
// Backed by `apps/operations/src/modules/reviews/reviews.controller.ts`. Use
// with `operationsClient`. The unified `list` endpoint feeds the dashboard
// (spec §12); per-row mutations target `ReviewResponse` via the `response/:id`
// sub-paths. Reply posting/drafting/mark-handled live on `ExternalReview` and
// are exposed under `reviewPlatformsV2Endpoints.replies.*` below.
export const reviewsV2Endpoints = {
  list: () => '/v2/reviews',
  stats: () => '/v2/reviews/stats',
  // Served by `ReviewMetricsController` under `modules/sales/contracts/reviews`
  // rather than the reviews module — the path is shared, the owning module is
  // not. `from` and `to` are required and must carry an explicit zone.
  funnelBaseline: () => '/v2/reviews/funnel-baseline',
  createJobStoriesShare: () => '/v2/reviews/stories/share',
  stories: () => '/v2/reviews/stories',
  detail: (source: 'response' | 'external' | 'testimonial', id: string) =>
    `/v2/reviews/${source}/${id}`,
  linkExternalAppointment: (id: string) => `/v2/reviews/external/${id}/appointment`,
  internalNote: (id: string) => `/v2/reviews/response/${id}/internal-note`,
  flag: (id: string) => `/v2/reviews/response/${id}/flag`,
  testimonialApproval: (id: string) => `/v2/reviews/testimonial/${id}/approval`,
  responseApproval: (id: string) => `/v2/reviews/response/${id}/approval`,
} as const;

// ─── Review Platforms v2 (operations service: /v2/review-platforms) ──────────
//
// Backed by:
//   - `apps/operations/src/modules/review-platforms/review-settings.controller.ts`
//   - `apps/operations/src/modules/review-platforms/oauth/oauth.controller.ts`
//   - `apps/operations/src/modules/review-platforms/replies/reply-poster.controller.ts`
// -- Third-party integrations (operations) -----------------------------------
export const companyCamEndpoints = {
  status: () => '/v2/integrations/companycam/status',
  authorizeUrl: () => '/v2/integrations/companycam/authorize-url',
  connection: () => '/v2/integrations/companycam/connection',
  settings: () => '/v2/integrations/companycam/settings',
  syncJob: (jobId: string) => `/v2/integrations/companycam/jobs/${jobId}/sync`,
};

export const reviewPlatformsV2Endpoints = {
  // ── ReviewSettings + PlatformConfig (Phase 2K) ──────────────────────────
  settings: () => '/v2/review-platforms/settings',
  platformConfig: (platformConfigId: string) => `/v2/review-platforms/settings/${platformConfigId}`,
  connectUrl: () => '/v2/review-platforms/settings/connect-url',
  disconnect: () => '/v2/review-platforms/settings/disconnect',
  reorder: () => '/v2/review-platforms/settings/reorder',
  setWriteEnabled: () => '/v2/review-platforms/settings/write-enabled',

  // ── OAuth flow ───────────────────────────────────────────────────────────
  oauthAuthorizationUrl: (platform: string) => `/v2/review-platforms/oauth/${platform}/url`,
  oauthAvailableBusinesses: (platform: string) =>
    `/v2/review-platforms/oauth/${platform}/available-businesses`,
  oauthSelectBusiness: (platform: string) =>
    `/v2/review-platforms/oauth/${platform}/select-business`,
  oauthDisconnect: (platform: string) => `/v2/review-platforms/oauth/${platform}/disconnect`,

  // ── Reply poster (Phase 2I) ──────────────────────────────────────────────
  // Each path takes the `ExternalReview.id` (UUID, no `ext_` prefix).
  replies: {
    draft: (externalReviewId: string) => `/v2/review-platforms/replies/${externalReviewId}/draft`,
    post: (externalReviewId: string) => `/v2/review-platforms/replies/${externalReviewId}/post`,
    markHandled: (externalReviewId: string) =>
      `/v2/review-platforms/replies/${externalReviewId}/mark-handled`,

    // ── Auto-post (NOT-576 §4.3) ──────────────────────────────────────────
    /** Everything auto-post would publish, and what is blocking the rest. */
    autoPostPending: (locationId?: string | null) =>
      locationId
        ? `/v2/review-platforms/replies/auto-post/pending?locationId=${encodeURIComponent(locationId)}`
        : '/v2/review-platforms/replies/auto-post/pending',
    autoPostCancel: (externalReviewId: string) =>
      `/v2/review-platforms/replies/${externalReviewId}/auto-post/cancel`,
  },

  setAutoPost: () => '/v2/review-platforms/settings/auto-post',
  /** Both reply switches in one request, so they cannot end up disagreeing. */
  setReplyMode: () => '/v2/review-platforms/settings/reply-mode',
  setAutoPostPaused: () => '/v2/review-platforms/settings/auto-post/paused',
} as const;

// ─── Tasks ────────────────────────────────────────────────────────────────────

export const taskEndpoints = {
  list: () => '/contact-tasks',
  counts: () => '/contact-tasks/counts',
  detail: (id: string) => `/contact-tasks/${id}`,
  create: (contactId: string) => `/contacts/${contactId}/tasks`,
  update: (id: string) => `/contact-tasks/${id}`,
  delete: (id: string) => `/contact-tasks/${id}`,
  updateStatus: (id: string) => `/contact-tasks/${id}/status`,
  /** Status change by the task's own assignee (technicians lack contacts:edit). */
  assigneeStatus: (id: string) => `/contact-tasks/${id}/assignee-status`,
  bulkComplete: () => '/contact-tasks/bulk-complete',
} as const;

// ─── Scheduler endpoints (served by operations on port 6129) ─────────────────
//
// These scheduler paths are now served by the operations microservice.

/**
 * Scheduling API version switch. `VITE_SCHEDULING_API_VERSION` is a runtime
 * config key (`/config.json` → `window.__APP_CONFIG__`, rewritten by the vite
 * `define` in vite.config.ts): `'v2'` routes the scheduling calls below at the
 * v1-compatible facade under `/v2/*`; anything else (unset, `'v1'`, …) keeps
 * the v1 paths byte-identical. Read at call time — never cached at module load
 * — so a test can stub the env and every builder follows.
 *
 * ONLY the bookings / availability / slots / conflicts / dispatch-move builders
 * consult it. Technicians, teams, routes, tracking, live dispatch, settings and
 * every `/jobs/*` path stay on v1 in both modes.
 */
export function schedulingPrefix(): '' | '/v2' {
  return import.meta.env.VITE_SCHEDULING_API_VERSION === 'v2' ? '/v2' : '';
}

export const schedulerEndpoints = {
  // Direct scheduler service endpoints (used by scheduler hooks). Versioned via
  // schedulingPrefix() — see the helper above for what is (and is not) covered.
  bookings: () => `${schedulingPrefix()}/bookings`,
  booking: (id: string) => `${schedulingPrefix()}/bookings/${id}`,
  bookingAlternatives: (id: string) => `${schedulingPrefix()}/bookings/${id}/alternatives`,
  bookingStatus: (id: string) => `${schedulingPrefix()}/bookings/${id}/status`,
  reschedule: (id: string) => `${schedulingPrefix()}/bookings/${id}/reschedule`,
  irisReschedule: (id: string) => `${schedulingPrefix()}/bookings/${id}/iris-reschedule`,
  move: (id: string) => `${schedulingPrefix()}/bookings/${id}/move`,
  schedule: (id: string) => `${schedulingPrefix()}/bookings/${id}/schedule`,
  suggestDispatch: (id: string) => `${schedulingPrefix()}/bookings/${id}/suggest-dispatch`,
  conflictsCheck: () => `${schedulingPrefix()}/conflicts/check`,
  approveCancellation: (id: string) => `${schedulingPrefix()}/bookings/${id}/approve-cancellation`,
  bookingAvailability: () => `${schedulingPrefix()}/availability`,
  // Scheduling v2 — day-level availability (ALL_DAY / DAY_FLEXIBLE), POST body.
  availabilityDays: () => `${schedulingPrefix()}/availability/days`,
  slots: () => `${schedulingPrefix()}/slots`,
  slot: (id: string) => `${schedulingPrefix()}/slots/${id}`,
  reserveSlot: (id: string) => `${schedulingPrefix()}/slots/${id}/reserve`,
  releaseSlot: (id: string) => `${schedulingPrefix()}/slots/${id}/release`,
  technicians: () => '/technicians',
  technician: (id: string) => `/technicians/${id}`,
  createTechnicianServiceArea: () => '/technicians/service-areas',
  technicianSearch: () => '/technicians/search',
  technicianServiceAreas: (id: string) => `/technicians/${id}/service-areas`,
  technicianExpertise: (id: string) => `/technicians/${id}/expertise`,
  technicianPhotoUploadUrl: (id: string) => `/technicians/${id}/photo/upload-url`,
  // GAP-02 — a technician's appointments (offboarding sheet passes ?upcoming=true).
  technicianAppointments: (id: string) => `/technicians/${id}/appointments`,
  serviceTypes: () => '/service-types',
  serviceType: (id: string) => `/service-types/${id}`,
  trades: () => '/trades',
  trade: (id: string) => `/trades/${id}`,
  // Provisioner trade catalog annotated per tenant.
  tradeCatalog: () => '/trades/catalog',
  tradeCatalogEnable: (key: string) => `/trades/catalog/${key}/enable`,
  tradeCatalogDisable: (key: string) => `/trades/catalog/${key}/disable`,
  // NOT-542 — Business Units (served by operations).
  businessUnits: () => '/business-units',
  businessUnit: (id: string) => `/business-units/${id}`,
  businessUnitArchive: (id: string) => `/business-units/${id}/archive`,
  businessUnitArchiveImpact: (id: string) => `/business-units/${id}/archive-impact`,
  businessUnitUnarchive: (id: string) => `/business-units/${id}/unarchive`,
  // Per-BU branding + template selections (Templating epic). Empty = inherit tenant.
  businessUnitBranding: (id: string) => `/business-units/${id}/branding`,
  businessUnitDocTemplates: (id: string) => `/business-units/${id}/doc-templates`,
  businessUnitEmailTemplates: (id: string) => `/business-units/${id}/email-templates`,
  oncalls: () => '/oncall',
  oncall: (id: string) => `/oncall/${id}`,
  emergencyDispatches: () => '/oncall/emergencies',
  multiScheduleAvailability: () => '/multi-schedule/availability',
  crews: () => '/crews',
  crew: (id: string) => `/crews/${id}`,
  crewAssign: (id: string) => `/crews/${id}/assign`,
  crewUnassign: (id: string) => `/crews/${id}/unassign`,
  crewAppointments: (id: string) => `/crews/${id}/appointments`,
  crewConflicts: (id: string) => `/crews/${id}/conflicts`,
  // Teams — board-organization grouping of technicians (distinct from crews).
  teams: () => '/teams',
  team: (id: string) => `/teams/${id}`,
  teamOrder: () => '/teams/order',
  teamAssign: (id: string) => `/teams/${id}/assign`,
  teamUnassign: (id: string) => `/teams/${id}/unassign`,
  teamMemberOrder: (id: string) => `/teams/${id}/technician-order`,
  // Dispatch board actions (§5/§6.6). Versioned (facade `v2/dispatch/*`);
  // the weather-hold and live-cockpit paths below are NOT.
  dispatchUnassign: () => `${schedulingPrefix()}/dispatch/unassign`,
  dispatchBulkReschedule: () => `${schedulingPrefix()}/dispatch/bulk-reschedule`,
  dispatchWeatherHold: () => '/dispatch/weather-hold',
  dispatchWeatherHoldPreview: () => '/dispatch/weather-hold/preview',
  dispatchWeatherSkip: () => '/dispatch/weather-skip',
  dispatchWeatherFlexPreview: () => '/dispatch/weather-flex/preview',
  dispatchWeatherFlexApply: () => '/dispatch/weather-flex/apply',
  dispatchWeatherReabsorbPreview: () => '/dispatch/weather-reabsorb/preview',
  dispatchWeatherReabsorbApply: () => '/dispatch/weather-reabsorb/apply',
  // Live dispatch cockpit (§15).
  dispatchLive: () => '/dispatch/live',
  // NOT-335 — a tech's geocoded stops for today (live map route polyline).
  dispatchLiveRoute: (technicianId: string) => `/dispatch/live/route/${technicianId}`,
  trackingAlerts: () => '/tracking/alerts',
  resolveTrackingAlert: (id: string) => `/tracking/alerts/${id}/resolve`,
  // NOT-336 — inline alert actions.
  notifyCustomerAlert: (id: string) => `/tracking/alerts/${id}/notify-customer`,
  messageTechAlert: (id: string) => `/tracking/alerts/${id}/message-tech`,
  // §9 — tech-side live location sharing (the "My Day" emitter surface).
  myTrackingDay: () => '/tracking/me/day',
  trackingPing: () => '/tracking/ping',
  trackingPause: () => '/tracking/pause',
  trackingArrived: (appointmentId: string) => `/tracking/appointment/${appointmentId}/arrived`,
  // Single-tech route optimization (§8.2).
  routeOptimize: () => '/routes/optimize',
  routeApply: () => '/routes/apply',
  routeApplyUndo: () => '/routes/apply-undo',
  routeReject: () => '/routes/reject',
  // Multi-tech fleet optimization (§8.3).
  routeOptimizeFleet: () => '/routes/optimize-fleet',
  routeAcceptPlan: () => '/routes/accept-plan',
  routeSummaries: () => '/routes/summaries',
  route: (technicianId: string, date: string) => `/routes/${technicianId}/${date}`,
  // Scheduling v2 phase 4 — ranked candidate route-days for moving a job.
  routeInsertionCandidates: (jobId: string) =>
    `/routes/insertion-candidates?jobId=${encodeURIComponent(jobId)}`,
  // §9 — create-form variant: rank route days by the typed address (no jobId yet).
  // The backend geocodes address/zipCode when lat/lng + jobId are absent.
  routeInsertionCandidatesByAddress: (params: {
    serviceType?: string;
    jobTypeId?: string;
    jobId?: string;
    address?: string;
    zipCode?: string;
    lat?: number;
    lng?: number;
    durationMinutes?: number;
    weekday?: number;
    technicianIds?: string[];
    dateFrom?: string;
    dateTo?: string;
    urgency?: 'standard' | 'urgent' | 'emergency';
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params.serviceType) q.set('serviceType', params.serviceType);
    if (params.jobTypeId) q.set('jobTypeId', params.jobTypeId);
    if (params.jobId) q.set('jobId', params.jobId);
    if (params.address) q.set('address', params.address);
    if (params.zipCode) q.set('zipCode', params.zipCode);
    if (Number.isFinite(params.lat)) q.set('lat', String(params.lat));
    if (Number.isFinite(params.lng)) q.set('lng', String(params.lng));
    if (params.durationMinutes != null) q.set('durationMinutes', String(params.durationMinutes));
    if (params.weekday != null) q.set('weekday', String(params.weekday));
    if (params.technicianIds?.length) q.set('technicianIds', params.technicianIds.join(','));
    if (params.dateFrom) q.set('dateFrom', params.dateFrom);
    if (params.dateTo) q.set('dateTo', params.dateTo);
    if (params.urgency) q.set('urgency', params.urgency);
    if (params.limit != null) q.set('limit', String(params.limit));
    return `/routes/insertion-candidates?${q.toString()}`;
  },
  // Scheduling v2 — a job's route membership. POST creates the initial
  // assignment (§6); PATCH moves an existing one (phase 4, tech / ISO weekday).
  // SINGLE SWAP POINT: the backend may relocate this to
  // `/routes/membership/${jobId}` for module-cycle reasons — change it here only.
  routeMembership: (jobId: string) => `/jobs/${jobId}/route-membership`,
  // Scheduling v2 phase 4 — nightly drift snapshots (from/to/technicianId are
  // query params, passed via the axios config).
  routeDrift: () => '/routes/drift',
  // Customer-coverage check used by <CoverageBanner /> on scheduling surfaces.
  coverage: () => '/scheduler/coverage',
  // Public (unauthenticated) coverage check used by the customer-facing
  // online-booking flow. The tenantId is passed in the URL since there's no
  // session header to scope the call.
  publicCoverage: (tenantId: string) => `/api/public/scheduler/coverage/${tenantId}`,
} as const;

// ─── Promotions (operations microservice) ────────────────────────────────────

export const promotionsEndpoints = {
  list: (status?: string) => (status ? `/promotions?status=${status}` : '/promotions'),
  listBase: () => '/promotions',
  detail: (id: string) => `/promotions/${id}`,
  create: () => '/promotions',
  update: (id: string) => `/promotions/${id}`,
  delete: (id: string) => `/promotions/${id}`,
  recordMention: (id: string) => `/promotions/${id}/mention`,
  recordConversion: (id: string) => `/promotions/${id}/conversion`,
} as const;

// ─── Audit logs (operations microservice) ────────────────────────────────────

export const auditLogEndpoints = {
  list: () => '/audit',
  // Filter by entity
  byEntity: (entityType: string, entityId: string) => `/audit/${entityType}/${entityId}`,
  stats: (entityType: string, entityId: string) => `/audit/${entityType}/${entityId}/stats`,
} as const;

// ─── Entity Types ─────────────────────────────────────────────────────────────

export const entityTypeEndpoints = {
  list: () => '/entity-types',
} as const;

// ─── Tags ─────────────────────────────────────────────────────────────────────

export const tagEndpoints = {
  list: (scope: string) => `/tags?scope=${encodeURIComponent(scope)}`,
  create: () => '/tags',
  update: (id: string) => `/tags/${id}`,
  delete: (id: string) => `/tags/${id}`,
} as const;

// ─── Operations Estimates ────────────────────────────────────────────────────

export const opsEstimateEndpoints = {
  list: () => '/estimates',
  detail: (id: string) => `/estimates/${id}`,
  create: () => '/estimates',
  createFromCatalog: () => '/estimates/from-catalog',
  evaluateUpsells: () => '/estimates/upsells/evaluate',
  update: (id: string) => `/estimates/${id}`,
  send: (id: string) => `/estimates/${id}/send`,
  approve: (id: string) => `/estimates/${id}/approve`,
  fulfill: (id: string) => `/estimates/${id}/fulfill`,
  customerApprove: (id: string) => `/estimates/${id}/customer-approve`,
  reject: (id: string) => `/estimates/${id}/reject`,
  delete: (id: string) => `/estimates/${id}`,
  convertToJob: (id: string) => `/estimates/${id}/convert-to-job`,
  reprice: (id: string) => `/estimates/${id}/reprice`,
  refresh: (id: string) => `/estimates/${id}/refresh-from-pricebook`,
  pdfUploadUrl: (id: string) => `/estimates/${id}/pdf-upload-url`,
  confirmPdf: (id: string) => `/estimates/${id}/pdf`,
  activePdf: (id: string) => `/estimates/${id}/pdf`,
  pdfDownloadUrl: (id: string) => `/estimates/${id}/pdf/download-url`,
  documentLayout: (id: string, previewDesign?: string) =>
    previewDesign
      ? `/estimates/${id}/document-layout?previewDesign=${encodeURIComponent(previewDesign)}`
      : `/estimates/${id}/document-layout`,
  pdfHistory: (id: string) => `/estimates/${id}/pdf/history`,
  publicPdf: (id: string) => `/estimates/public/${id}/pdf`,
  pendingAttachmentUploadUrl: () => `/estimates/pending-attachment-upload-url`,
  attachmentUploadUrl: (id: string) => `/estimates/${id}/attachments/upload-url`,
  attachments: (id: string) => `/estimates/${id}/attachments`,
  attachmentImages: (id: string) => `/estimates/${id}/attachments/images`,
  invalidatePdf: (id: string) => `/estimates/${id}/pdf`,
  publicAttachmentImages: (id: string) => `/estimates/public/${id}/attachments/images`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/estimates/${id}/attachments/${attachmentId}`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/estimates/${id}/attachments/${attachmentId}/download-url`,
  customFieldValues: (id: string) => `/estimates/${id}/custom-field-values`,
  tags: (id: string) => `/estimates/${id}/tags`,
  nextNumber: () => '/estimates/next-number',
  copyAttachments: (id: string) => `/estimates/${id}/copy-attachments`,
  revise: (id: string) => `/estimates/${id}/revise`,
  followUp: (id: string) => `/estimates/${id}/follow-up`,
} as const;

// ─── Operations Proposals ────────────────────────────────────────────────────

export const opsProposalEndpoints = {
  list: () => '/proposals',
  detail: (id: string) => `/proposals/${id}`,
  create: () => '/proposals',
  update: (id: string) => `/proposals/${id}`,
  delete: (id: string) => `/proposals/${id}`,
  send: (id: string) => `/proposals/${id}/send`,
  decline: (id: string) => `/proposals/${id}/decline`,
  selectOption: (id: string) => `/proposals/${id}/select-option`,
  acceptInPerson: (id: string) => `/proposals/${id}/accept-in-person`,
  presentation: (id: string) => `/proposals/${id}/presentation`,
  materialize: (id: string) => `/proposals/${id}/materialize-estimate`,
  revise: (id: string) => `/proposals/${id}/revise`,
  followUp: (id: string) => `/proposals/${id}/follow-up`,
  pendingAttachmentUploadUrl: () => `/proposals/pending-attachment-upload-url`,
  attachmentUploadUrl: (id: string) => `/proposals/${id}/attachments/upload-url`,
  attachments: (id: string) => `/proposals/${id}/attachments`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/proposals/${id}/attachments/${attachmentId}`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/proposals/${id}/attachments/${attachmentId}/download-url`,
} as const;

// ─── Estimate Templates ─────────────────────────────────────────────────────

export interface ReportPeriodParams {
  from?: string;
  to?: string;
  ownerId?: string;
  technicianId?: string;
  jobTypeId?: string;
}

export interface JobHotspotReportParams extends ReportPeriodParams {
  jobTypeId?: string;
  lowVolumeThreshold?: number;
}

function reportPeriodQuery(params?: ReportPeriodParams): string {
  const search = new URLSearchParams();
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  if (params?.ownerId) search.set('ownerId', params.ownerId);
  if (params?.technicianId) search.set('technicianId', params.technicianId);
  if (params?.jobTypeId) search.set('jobTypeId', params.jobTypeId);
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const estimateAnalyticsReportEndpoints = {
  salesSummary: (params?: ReportPeriodParams) =>
    `/reports/estimates/sales-summary${reportPeriodQuery(params)}`,
} as const;

export const estimateTemplateEndpoints = {
  list: () => '/estimate-templates',
  detail: (id: string) => `/estimate-templates/${id}`,
  create: () => '/estimate-templates',
  update: (id: string) => `/estimate-templates/${id}`,
  delete: (id: string) => `/estimate-templates/${id}`,
  materialize: (id: string) => `/estimate-templates/${id}/materialize-estimate`,
  attachmentUploadUrl: (id: string) => `/estimate-templates/${id}/attachments/upload-url`,
  attachments: (id: string) => `/estimate-templates/${id}/attachments`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/estimate-templates/${id}/attachments/${attachmentId}`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/estimate-templates/${id}/attachments/${attachmentId}/download-url`,
  copyAttachments: (id: string) => `/estimate-templates/${id}/copy-attachments`,
} as const;

// ─── Estimate Document Templates (block builder) ────────────────────────────

export const estimateDocTemplateEndpoints = {
  list: () => '/estimate-doc-templates',
  detail: (id: string) => `/estimate-doc-templates/${id}`,
  create: () => '/estimate-doc-templates',
  update: (id: string) => `/estimate-doc-templates/${id}`,
  delete: (id: string) => `/estimate-doc-templates/${id}`,
  publish: (id: string) => `/estimate-doc-templates/${id}/publish`,
  clone: (id: string) => `/estimate-doc-templates/${id}/clone`,
  setDefault: (id: string) => `/estimate-doc-templates/${id}/default`,
  uploadLogo: () => '/estimate-doc-templates/logo',
  // Resolve the layout for a PICKED design (create-flow pre-save preview, or the
  // BU editor). Omit / 'default' → tenant default; a uuid → that design.
  // `businessUnitId` applies that BU's branding; `docType` selects the surface.
  previewLayout: (
    docTemplateId?: string | null,
    opts?: { businessUnitId?: string | null; docType?: 'ESTIMATE' | 'INVOICE' },
  ) => {
    const params = new URLSearchParams();
    if (docTemplateId) params.set('docTemplateId', docTemplateId);
    if (opts?.businessUnitId) params.set('businessUnitId', opts.businessUnitId);
    if (opts?.docType) params.set('docType', opts.docType);
    const qs = params.toString();
    return `/estimate-doc-templates/preview-layout${qs ? `?${qs}` : ''}`;
  },
} as const;

// ─── Email Templates (standalone send-email designs) ────────────────────────

export const emailTemplateEndpoints = {
  list: (params?: { kind?: string }) => {
    const q = new URLSearchParams();
    if (params?.kind) q.set('kind', params.kind);
    const qs = q.toString();
    return `/email-templates${qs ? `?${qs}` : ''}`;
  },
  detail: (id: string) => `/email-templates/${id}`,
  /** Built-in default content for a kind — the preview fallback when unconfigured. */
  default: (kind: string) => `/email-templates/defaults/${kind}`,
  create: () => '/email-templates',
  /** Platform starters every tenant can clone into its own DRAFT design. */
  starters: (kind?: string) => `/email-templates/starters${kind ? `?kind=${kind}` : ''}`,
  useStarter: (slug: string) => `/email-templates/starters/${slug}/use`,
  update: (id: string) => `/email-templates/${id}`,
  publish: (id: string) => `/email-templates/${id}/publish`,
  setDefault: (id: string) => `/email-templates/${id}/default`,
  delete: (id: string) => `/email-templates/${id}`,
  businessUnits: (id: string) => `/email-templates/${id}/business-units`,
} as const;

// ─── Proposal Templates ─────────────────────────────────────────────────────

export const proposalTemplateEndpoints = {
  list: () => '/proposal-templates',
  detail: (id: string) => `/proposal-templates/${id}`,
  create: () => '/proposal-templates',
  update: (id: string) => `/proposal-templates/${id}`,
  delete: (id: string) => `/proposal-templates/${id}`,
  attachmentUploadUrl: (id: string) => `/proposal-templates/${id}/attachments/upload-url`,
  attachments: (id: string) => `/proposal-templates/${id}/attachments`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/proposal-templates/${id}/attachments/${attachmentId}`,
  downloadAttachment: (id: string, attachmentId: string) =>
    `/proposal-templates/${id}/attachments/${attachmentId}/download-url`,
  copyAttachments: (id: string) => `/proposal-templates/${id}/copy-attachments`,
} as const;

// ─── Jobs ────────────────────────────────────────────────────────────────────

export const jobEndpoints = {
  list: () => '/jobs',
  detail: (id: string) => `/jobs/${id}`,
  // NOT-339 §12 — budget-vs-actual cost summary.
  costSummary: (id: string) => `/jobs/${id}/cost-summary`,
  // §15 — lifetime value / paid / outstanding / scheduled revenue.
  financialSummary: (id: string) => `/jobs/${id}/financial-summary`,
  // A prepaid series' term invoice, billed once.
  billPrepaidTerm: (id: string) => `/jobs/${id}/prepaid-term`,
  // §13 — is a customer portal link live for this job's customer.
  portalStatus: (id: string) => `/jobs/${id}/portal-status`,
  // §8 — escalation summary + tax-jurisdiction label for the recurring detail.
  recurringMeta: (id: string) => `/jobs/${id}/recurring-meta`,
  // Reverse-chron activity feed for the job Activity tab.
  activity: (id: string) => `/jobs/${id}/activity`,
  create: () => '/jobs',
  recurringAvailabilityPlan: () => '/jobs/recurring/availability-plan',
  recurringRoutePlan: () => '/jobs/recurring/route-plan',
  update: (id: string) => `/jobs/${id}`,
  addItem: (id: string) => `/jobs/${id}/items`,
  updateItem: (id: string, itemId: string) => `/jobs/${id}/items/${itemId}`,
  deleteItem: (id: string, itemId: string) => `/jobs/${id}/items/${itemId}`,
  payments: (id: string) => `/jobs/${id}/payments`,
  paymentRequestLink: (id: string) => `/jobs/${id}/payment-request-link`,
  createApprovalEstimate: (id: string) => `/jobs/${id}/approval-estimate`,
  // NOT-560 — create an estimate on a SERVICE_CALL job (fan-out; sourceJobId).
  createServiceCallEstimate: (id: string) => `/jobs/${id}/service-call-estimate`,
  related: (id: string) => `/jobs/${id}/related`,
  // A job has no files of its own — they hang off its notes (see noteEndpoints).
  // The one exception is the create form, which has no job (and so no note) to
  // upload against yet; these are adopted onto a note when the job is saved.
  pendingAttachmentUploadUrl: () => '/jobs/pending-attachment-upload-url',
  deletePendingAttachment: (attachmentId: string) => `/jobs/pending-attachments/${attachmentId}`,
  // NOT-573 — media roll-up: all visits' media + job-level items, by reference.
  media: (id: string) => `/jobs/${id}/media`,
  deleteMedia: (id: string, attachmentId: string) => `/jobs/${id}/media/${attachmentId}`,
  schedule: (id: string) => `/jobs/${id}/schedule`,
  start: (id: string) => `/jobs/${id}/start`,
  hold: (id: string) => `/jobs/${id}/hold`,
  complete: (id: string) => `/jobs/${id}/complete`,
  cancel: (id: string) => `/jobs/${id}/cancel`,
  restore: (id: string) => `/jobs/${id}/restore`,
  archive: (id: string) => `/jobs/${id}/archive`,
  invoice: (id: string) => `/jobs/${id}/invoice`,
  close: (id: string) => `/jobs/${id}/close`,
  appointments: (id: string) => `/jobs/${id}/appointments`,
  createAppointment: (id: string) => `/jobs/${id}/appointments`,
  appointment: (id: string, appointmentId: string) => `/jobs/${id}/appointments/${appointmentId}`,
  appointmentTransition: (id: string, appointmentId: string) =>
    `/jobs/${id}/appointments/${appointmentId}/transition`,
  appointmentAssign: (id: string, appointmentId: string) =>
    `/jobs/${id}/appointments/${appointmentId}/assign`,
  // GAP-02 (Part A) — free a visit's crew (needs-tech) from the job context.
  appointmentUnassign: (id: string, appointmentId: string) =>
    `/jobs/${id}/appointments/${appointmentId}/unassign`,
  // GAP-01 — assign one crew to several visits of a job in a single action.
  appointmentsAssignBatch: (id: string) => `/jobs/${id}/appointments/assign-batch`,
  // GAP-02 (Delta 2) — technicians skill-qualified to lead a cross-job selection.
  qualifiedTechnicians: () => `/jobs/appointments/qualified-technicians`,
  // GAP-02 — surgical crew swap (offboarding): replace only the departing tech.
  appointmentsReassignTechnicianBatch: (id: string) =>
    `/jobs/${id}/appointments/reassign-technician-batch`,
  appointmentConfirm: (id: string, appointmentId: string) =>
    `/jobs/${id}/appointments/${appointmentId}/confirm`,
  appointmentSkip: (id: string, appointmentId: string) =>
    `/jobs/${id}/appointments/${appointmentId}/skip`,
  // Take named objections off a visit's "Acknowledged scheduling issues" note.
  appointmentDismissAcknowledgedIssues: (id: string, appointmentId: string) =>
    `/jobs/${id}/appointments/${appointmentId}/acknowledged-issues/dismiss`,
  transition: (id: string) => `/jobs/${id}/transition`,
  authorization: (id: string) => `/jobs/${id}/authorization`,
  authorizationCollect: (id: string) => `/jobs/${id}/authorization/collect`,
  signoff: (id: string) => `/jobs/${id}/signoff`,
  signoffCollect: (id: string) => `/jobs/${id}/signoff/collect`,
  // NOT-339 §4.4 — preview a recurring job's upcoming (not-yet-materialized) visits.
  upcomingVisits: (id: string) => `/jobs/${id}/visits/upcoming`,
} as const;

// NOT-339 §8 / §15 — recurring series lifecycle on the parent Job (/v2/jobs).
// Distinct from the one-off pipeline cancel (jobEndpoints.cancel).
export const recurringJobEndpoints = {
  activate: (id: string) => `/v2/jobs/${id}/activate`,
  pause: (id: string) => `/v2/jobs/${id}/pause`,
  resume: (id: string) => `/v2/jobs/${id}/resume`,
  cancel: (id: string) => `/v2/jobs/${id}/cancel`,
  withdrawCancellation: (id: string) => `/v2/jobs/${id}/cancel/withdraw`,
  cancellationPreview: (id: string) => `/v2/jobs/${id}/cancellation-preview`,
  saveOffer: (id: string) => `/v2/jobs/${id}/save-offer`,
  renew: (id: string) => `/v2/jobs/${id}/renew`,
} as const;

// NOT-339 §12 — production-rate kits.
export const productionKitEndpoints = {
  list: () => '/production-kits',
  detail: (id: string) => `/production-kits/${id}`,
  create: () => '/production-kits',
  update: (id: string) => `/production-kits/${id}`,
  remove: (id: string) => `/production-kits/${id}`,
} as const;

export const costingSettingsEndpoints = {
  get: () => '/settings/costing',
  update: () => '/settings/costing',
} as const;

export const costAnalyticsReportEndpoints = {
  budgetVsActual: (params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const s = q.toString();
    return `/reports/costing/budget-vs-actual${s ? `?${s}` : ''}`;
  },
  weatherImpact: (params?: {
    from?: string;
    to?: string;
    territoryId?: string;
    groupBy?: 'territory' | 'month' | 'route';
  }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    if (params?.territoryId) q.set('territoryId', params.territoryId);
    if (params?.groupBy) q.set('groupBy', params.groupBy);
    const s = q.toString();
    return `/reports/costing/weather-impact${s ? `?${s}` : ''}`;
  },
} as const;

export const billingAlertEndpoints = {
  list: (params?: { includeResolved?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.includeResolved) q.set('includeResolved', 'true');
    const s = q.toString();
    return `/billing/alerts${s ? `?${s}` : ''}`;
  },
  resolve: (id: string) => `/billing/alerts/${id}/resolve`,
} as const;

// NOT-339 §9 / §15 — recurring review engine (System A), first FE surface.
export const reviewEndpoints = {
  jobReviews: (jobId: string) => `/jobs/${jobId}/reviews`,
  manualTrigger: (appointmentId: string) => `/visits/${appointmentId}/review-request`,
  rules: () => '/review-rules',
  eligibility: (customerId: string) => `/customers/${customerId}/review-eligibility`,
} as const;

// ─── Appointment Authorization/Sign-off ──────────────────────────────────────

export const appointmentAuthEndpoints = {
  authorization: (id: string) => `/appointments/${id}/authorization`,
  authorizationCollect: (id: string) => `/appointments/${id}/authorization/collect`,
  signoff: (id: string) => `/appointments/${id}/signoff`,
  signoffCollect: (id: string) => `/appointments/${id}/signoff/collect`,
} as const;

// ─── Signature capture (presigned S3 upload) ──────────────────────────────────

export const signatureEndpoints = {
  uploadUrl: () => `/authorization/signature-upload-url`,
} as const;

// ─── Recurring Billing ──────────────────────────────────────────────────────

export const recurringBillingEndpoints = {
  plans: () => '/billing/recurring/plans',
  plan: (id: string) => `/billing/recurring/plans/${id}`,
  pausePlan: (id: string) => `/billing/recurring/plans/${id}/pause`,
  resumePlan: (id: string) => `/billing/recurring/plans/${id}/resume`,
  runPlanNow: (id: string) => `/billing/recurring/plans/${id}/run-now`,
  retryPlanFailure: (id: string) => `/billing/recurring/plans/${id}/retry-last-failure`,
  // A completed per-visit visit with no live invoice (e.g. voided), billed again.
  billVisitAgain: (appointmentId: string) =>
    `/billing/recurring/visits/${appointmentId}/bill-again`,
  updatePlanPaymentMethod: (id: string) => `/billing/recurring/plans/${id}/payment-method`,
  createRepairLink: (id: string) => `/billing/recurring/plans/${id}/payment-method-repair-link`,
  runs: () => '/billing/recurring/runs',
  run: (id: string) => `/billing/recurring/runs/${id}`,
  exceptions: () => '/billing/recurring/exceptions',
  dispatchNow: () => '/billing/recurring/dispatch',
} as const;

// ─── Office Recovery & Renewals ─────────────────────────────────────────────

export const officeRecoveryRenewalEndpoints = {
  recoveryQueue: () => '/office/recovery-queue',
  renewalQueue: () => '/office/renewal-queue',
} as const;

// ─── Membership Tune-Ups (NOT-598) ───────────────────────────────────────────

export const tuneUpEndpoints = {
  queue: () => '/tune-ups/queue',
  dismiss: (id: string) => `/tune-ups/queue/${id}/dismiss`,
  bulk: () => '/tune-ups/queue/bulk',
  redemptionReport: () => '/tune-ups/reports/redemption',
  settings: () => '/tune-ups/settings',
} as const;

// ─── Job Pipelines ───────────────────────────────────────────────────────────

export const jobPipelineEndpoints = {
  list: () => '/job-pipelines',
  detail: (id: string) => `/job-pipelines/${id}`,
  create: () => '/job-pipelines',
  update: (id: string) => `/job-pipelines/${id}`,
  delete: (id: string) => `/job-pipelines/${id}`,
  stages: (id: string) => `/job-pipelines/${id}/stages`,
  stage: (id: string, stageId: string) => `/job-pipelines/${id}/stages/${stageId}`,
} as const;

// ─── Time Entries (appointment-scoped, operations service) ────────────────

export const timeEntryEndpoints = {
  clockIn: (appointmentId: string) => `/appointments/${appointmentId}/time-entries/clock-in`,
  // Office / crew-lead clock-in for a SELECTED technician. Appointment is
  // OPTIONAL in the body — omit it to start OVERHEAD (non-job) time.
  clockInSelected: () => '/time-entries/clock-in',
  clockOut: (id: string) => `/time-entries/${id}/clock-out`,
  appointmentList: (appointmentId: string) => `/appointments/${appointmentId}/time-entries`,
  detail: (id: string) => `/time-entries/${id}`,
  update: (id: string) => `/time-entries/${id}`,
  delete: (id: string) => `/time-entries/${id}`,
  jobRollup: (jobId: string) => `/jobs/${jobId}/time-entries`,
} as const;

// ─── Appointments (per-visit detail mutations, operations service) ──────────

export const appointmentEndpoints = {
  outcome: (id: string) => `/appointments/${id}/outcome`,
  arrivalWindow: (id: string) => `/appointments/${id}/arrival-window`,
  additionalItems: (id: string) => `/appointments/${id}/additional-items`,
  additionalItem: (id: string, snapshotId: string) =>
    `/appointments/${id}/additional-items/${snapshotId}`,
  removedBaseItems: (id: string) => `/appointments/${id}/removed-base-items`,
  removedBaseItem: (id: string, snapshotId: string) =>
    `/appointments/${id}/removed-base-items/${snapshotId}`,
  confirmation: (id: string) => `/appointments/${id}/confirmation`,
  customerNotes: (id: string) => `/appointments/${id}/customer-notes`,
  notes: (id: string) => `/appointments/${id}/notes`,
  // Scheduling v2 §5 — multi-day ALL_DAY series actions (remaining days only).
  seriesReschedule: (seriesId: string) => `/appointments/series/${seriesId}/reschedule`,
  seriesReassign: (seriesId: string) => `/appointments/series/${seriesId}/reassign`,
  seriesCancel: (seriesId: string) => `/appointments/series/${seriesId}/cancel`,
} as const;

// ─── Payment Schedules (spec §5, operations service) ────────────────────────

export const paymentScheduleEndpoints = {
  detail: (id: string) => `/payment-schedules/${id}`,
  update: (id: string) => `/payment-schedules/${id}`,
  pause: (id: string) => `/payment-schedules/${id}/pause`,
  resume: (id: string) => `/payment-schedules/${id}/resume`,
  byJob: (jobId: string) => `/jobs/${jobId}/payment-schedule`,
} as const;

// ─── Auto-charge Consent Records (spec §5.5, operations service) ────────────

export const consentEndpoints = {
  capture: () => '/consent-records',
  listForContact: (contactId: string) => `/contacts/${contactId}/consent-records`,
  revoke: (id: string) => `/consent-records/${id}/revoke`,
} as const;

// ─── Expenses (job-scoped, operations service) ─────────────────────────────

export const expenseEndpoints = {
  listByJob: (jobId: string) => `/jobs/${jobId}/expenses`,
  createForJob: (jobId: string) => `/jobs/${jobId}/expenses`,
  profitability: (jobId: string) => `/jobs/${jobId}/profitability`,
  update: (id: string) => `/expenses/${id}`,
  delete: (id: string) => `/expenses/${id}`,
  pendingAttachmentUploadUrl: () => '/expenses/pending-attachment-upload-url',
  attachmentUploadUrl: (id: string) => `/expenses/${id}/attachments/upload-url`,
  attachments: (id: string) => `/expenses/${id}/attachments`,
  deleteAttachment: (id: string, attachmentId: string) =>
    `/expenses/${id}/attachments/${attachmentId}`,
  attachmentDownloadUrl: (id: string, attachmentId: string) =>
    `/expenses/${id}/attachments/${attachmentId}/download-url`,
  approve: (id: string) => `/expenses/${id}/approve`,
  reject: (id: string) => `/expenses/${id}/reject`,
  submit: (jobId: string, expenseId: string) => `/jobs/${jobId}/expenses/${expenseId}/submit`,
  cancel: (jobId: string, expenseId: string) => `/jobs/${jobId}/expenses/${expenseId}/cancel`,
  void: (jobId: string, expenseId: string) => `/jobs/${jobId}/expenses/${expenseId}/void`,
  reimbursementApprove: (jobId: string, expenseId: string) =>
    `/jobs/${jobId}/expenses/${expenseId}/reimbursement/approve`,
  reimbursementPay: (jobId: string, expenseId: string) =>
    `/jobs/${jobId}/expenses/${expenseId}/reimbursement/pay`,
} as const;

// ─── Tax Zones & Groups ─────────────────────────────────────────────────────

export const taxZoneEndpoints = {
  list: () => '/tax-zones',
  lookup: () => '/tax-zones/lookup',
  // Operator-defined zone: typed rates + ZIP list, no tax data provider.
  createManual: () => '/tax-zones/manual',
  populate: () => '/tax-zones/populate',
  summary: () => '/tax-zones/summary',
  bulkDelete: () => '/tax-zones/bulk-delete',
  byId: (id: string) => `/tax-zones/${id}`,
  // NOT-95 — polygon-driven preview (read-only, single sync round-trip).
  // Kept for backward compatibility; the dialog uses the split flow below.
  previewFromPolygon: () => '/tax-zones/preview-from-polygon',
  // NOT-99 — split preview, Phase A: polygon → ZIPs (sub-second, in-memory).
  previewFromPolygonZips: () => '/tax-zones/preview-from-polygon-zips',
  // NOT-99 — split preview, Phase B: enqueue a Ziptax fan-out + rollup job.
  // 202 Accepted, returns `{ jobId, statusEndpoint }` — poll for the result.
  previewRates: () => '/tax-zones/preview-rates',
  previewRatesStatus: (jobId: string) => `/tax-zones/preview-rates/${jobId}`,
  // NOT-95 step 5 — writeback that persists the admin-confirmed authority
  // picks into TaxCode + TaxGroup + per-ZIP TaxZoneIndex rows.
  createFromPolygon: () => '/tax-zones/create-from-polygon',
  // NOT-130 — per-ZIP override surface + tenant-wide overrides feed.
  // PATCH with `{ rateOverride?: number | null; appliesToOverride?:
  // 'all'|'materials'|'labor'|null }`. `null` per field clears it.
  zoneOverride: (zipCode: string) => `/tax-zones/${encodeURIComponent(zipCode)}/override`,
  // GET — flat list of every active override across the tenant.
  overrides: () => '/tax-zones/overrides',
} as const;

export const taxGroupEndpoints = {
  list: () => '/tax-groups',
  detail: (id: string) => `/tax-groups/${id}`,
  setDefault: (id: string) => `/tax-groups/${id}/set-default`,
} as const;

// Tax Policy layer (phase 4) — per-BU / residential-commercial tax rules
// plus the per-document MANUAL tax-group writer.
export const taxPolicyEndpoints = {
  list: () => '/tax-policies',
  detail: (id: string) => `/tax-policies/${id}`,
  create: () => '/tax-policies',
  update: (id: string) => `/tax-policies/${id}`,
  remove: (id: string) => `/tax-policies/${id}`,
} as const;

export const documentTaxGroupEndpoints = {
  estimate: (id: string) => `/estimates/${id}/tax-group`,
  job: (id: string) => `/jobs/${id}/tax-group`,
  invoice: (id: string) => `/invoices/${id}/tax-group`,
} as const;

/**
 * A stored estimate or invoice as tax-simulator input (read-only): its tax
 * context and the bases its pricing taxes, for trying a tax rule against it.
 */
export const documentTaxPreviewEndpoints = {
  estimate: (id: string) => `/estimates/${id}/tax-preview-input`,
  invoice: (id: string) => `/invoices/${id}/tax-preview-input`,
  /** POST — the estimate repriced with a tax rule (nothing is written). */
  estimateRepriced: (id: string) => `/estimates/${id}/tax-preview`,
  /** POST — the invoice repriced with a tax rule (nothing is written). */
  invoiceRepriced: (id: string) => `/invoices/${id}/tax-preview`,
} as const;

export const taxCodeEndpoints = {
  list: () => '/tax-codes',
  detail: (id: string) => `/tax-codes/${id}`,
  create: () => '/tax-codes',
  update: (id: string) => `/tax-codes/${id}`,
  restoreRate: (id: string) => `/tax-codes/${id}/restore-rate`,
  // Revert a tenant-created tax code: soft-deletes the code, drops the
  // group link, and — if the code was the only customization on a fork
  // clone — re-points the affected zones back to the parent and
  // soft-deletes the clone. Used by the city-tier pencil's Revert
  // button (Brian's NOT- bug).
  revert: (id: string) => `/tax-codes/${id}/revert`,
  // Per-tenant applies-to override. PATCH with body `{ override: 'all' |
  // 'materials' | 'labor' | null }`. `null` = restore to Ziptax-derived.
  setAppliesToOverride: (id: string) => `/tax-codes/${id}/applies-to-override`,
  // No-persist tax preview used by the Tax Simulator page. Reuses the
  // live tax-resolution + tax-calculation services to mirror invoice
  // pricing exactly (see `tax-simulate.service.ts`).
  simulate: () => '/tax-codes/simulate',
  delete: (id: string) => `/tax-codes/${id}`,
} as const;

// ─── Service Areas ──────────────────────────────────────────────────────────

export const onboardingEndpoints = {
  status: () => '/onboarding/status',
};

export const serviceAreaEndpoints = {
  list: () => '/service-areas',
  coverage: () => '/service-areas/coverage',
  create: () => '/service-areas',
  delete: (id: string) => `/service-areas/${id}`,
  zipLookup: (zip: string) => `/service-areas/zip-lookup/${zip}`,
  // Free-text place search (neighborhood / landmark / city / ZIP / address) →
  // the ZIP(s) it would add to coverage, each enriched with its named area.
  // See `apps/operations/.../service-areas.controller.ts` `placeSearch`.
  placeSearch: (q: string) => `/service-areas/place-search?q=${encodeURIComponent(q)}`,
  postalCodes: () => '/service-areas/postal-codes',
  bulk: () => '/service-areas/bulk',
  // Canada region-picker — alternative to the US polygon-draw flow.
  // See `CanadaRegionPicker` and `apps/operations/src/modules/taxes/
  // service-areas/canada-regions/canada-regions.controller.ts`.
  // The `regions` endpoint now returns the per-city FSA list, so the FE
  // can compute checkbox state in-memory and no longer needs to POST to
  // `/canada/expand` on every click.
  canadaRegions: () => '/service-areas/canada/regions',
  // US region-picker — the "Cities" alternative to the US polygon-draw
  // flow. Mirrors the Canada region-picker contract exactly so the FE
  // can keep one shared picker shell with a swap of label strings.
  // See `UsRegionPicker` and `apps/operations/src/modules/taxes/
  // service-areas/us-regions/us-regions.controller.ts`.
  usRegions: () => '/service-areas/us/regions',
  // Lightweight polygon → ZIP preview for the service-area picker. Returns
  // only the resolved ZIP list (no rates) so the request stays sub-second
  // even on populated US regions. Rate population happens async via the
  // `/tax-zones/populate` BullMQ job kicked off after the user saves.
  previewFromPolygon: () => '/service-areas/preview-from-polygon',
} as const;

// ─── Territories (scheduler — named groupings of zip service areas) ──────────

export const territoryEndpoints = {
  list: () => '/territories',
  tree: () => '/territories/tree',
  resolve: () => '/territories/resolve',
  create: () => '/territories',
  detail: (id: string) => `/territories/${id}`,
  update: (id: string) => `/territories/${id}`,
  archive: (id: string) => `/territories/${id}`,
  techs: (id: string) => `/territories/${id}/techs`,
  zips: (id: string) => `/territories/${id}/zips`,
  setZips: (id: string) => `/territories/${id}/zips`,
} as const;

// ─── Capacity Rules (scheduler — team-wide + time-block caps) ────────────────

export const capacityRuleEndpoints = {
  list: () => '/capacity-rules',
  create: () => '/capacity-rules',
  update: (id: string) => `/capacity-rules/${id}`,
  archive: (id: string) => `/capacity-rules/${id}`,
  check: () => '/capacity/check',
} as const;

// ─── Custom Fields (operations service — define-once, associate-many) ────────

export const customFieldEndpoints = {
  // Definitions
  definitions: () => '/api/custom-fields/definitions',
  definition: (id: string) => `/api/custom-fields/definitions/${id}`,
  // Associations
  schema: () => '/api/custom-fields/schema',
  definitionAssociations: (defId: string) => `/api/custom-fields/definitions/${defId}/associations`,
  association: (assocId: string) => `/api/custom-fields/associations/${assocId}`,
  reorderAssociations: () => '/api/custom-fields/associations/reorder',
  // Inheritance
  inheritanceRules: () => '/api/custom-fields/inheritance-rules',
  inheritanceRule: (id: string) => `/api/custom-fields/inheritance-rules/${id}`,
  // Values
  values: () => '/api/custom-fields/values',
  bulkValues: () => '/api/custom-fields/values/bulk',
  filter: () => '/api/custom-fields/filter',
} as const;

// ─── Credit Memos (spec §8) ───────────────────────────────────────────────────

export const creditMemoEndpoints = {
  list: () => '/credit-memos',
  detail: (id: string) => `/credit-memos/${id}`,
  create: () => '/credit-memos',
  apply: (id: string) => `/credit-memos/${id}/apply`,
  approve: (id: string) => `/credit-memos/${id}/approve`,
  cashOut: (id: string) => `/credit-memos/${id}/cash-out`,
  void: (id: string) => `/credit-memos/${id}/void`,
  autoSuggest: (contactId: string) => `/contacts/${contactId}/credit-memos/auto-suggest`,
} as const;

// ─── Receipts (Wave 2 — spec §6.3) ────────────────────────────────────────────

// Public (unauthenticated) receipt endpoints — used by the customer-facing
// `/receipt/$id` route. `detail` returns the snapshot; `pdf` is a redirect
// to a freshly signed S3 URL so the bucket hostname stays out of the page.
export const publicReceiptEndpoints = {
  detail: (id: string) => `/api/public/receipts/${id}`,
  pdf: (id: string) => `/api/public/receipts/${id}/pdf`,
} as const;

// ─── Billing V2 (spec §11.1, PR 10) ──────────────────────────────────────────
//
// New `/v2/billing/*` namespace. Mirrors the legacy `/invoices`,
// `/payments`, etc. paths but with idempotency required on
// money-moving POSTs. Use the `billingV2Post` wrapper from
// `clients/billing-v2.ts` to auto-attach the `Idempotency-Key`
// header. Legacy paths stay live during the deprecation window;
// see `MIGRATION.md` for the swap pattern.
export const billingV2Endpoints = {
  // Invoices
  invoiceCreate: () => '/v2/billing/invoices',
  invoiceUpdate: (id: string) => `/v2/billing/invoices/${id}`,
  invoiceSend: (id: string) => `/v2/billing/invoices/${id}/send`,
  invoiceVoid: (id: string) => `/v2/billing/invoices/${id}/void`,
  invoiceWriteOff: (id: string) => `/v2/billing/invoices/${id}/write-off`,
  // Payments
  paymentCreate: () => '/v2/billing/payments',
  paymentRefund: (id: string) => `/v2/billing/payments/${id}/refund`,
  // Apply a customer's available credit (unapplied payments) to an invoice
  paymentApplyCredit: () => '/v2/billing/payments/apply-credit',
  // Deposits
  depositCapture: () => '/v2/billing/deposits',
  depositApply: (id: string) => `/v2/billing/deposits/${id}/apply`,
  depositRefund: (id: string) => `/v2/billing/deposits/${id}/refund`,
  // Credit memos
  creditMemoCreate: () => '/v2/billing/credit-memos',
  creditMemoApply: (id: string) => `/v2/billing/credit-memos/${id}/apply`,
  // Payment methods
  paymentMethodSetupIntent: () => '/v2/billing/payment-methods/setup-intent',
  // Payment schedules
  paymentScheduleCreate: () => '/v2/billing/payment-schedules',
  paymentScheduleFireRule: (scheduleId: string, ruleId: string) =>
    `/v2/billing/payment-schedules/${scheduleId}/fire-rule/${ruleId}`,
  // QBO
  qboSync: (entity: string, id: string) => `/v2/billing/qbo/sync/${entity}/${id}`,
  qboReconciliation: () => '/v2/billing/qbo/reconciliation',
} as const;

// ─── Barrel ───────────────────────────────────────────────────────────────────

/**
 * All endpoint groups in one object for ergonomic import.
 * @example
 *   import { endpoints } from '@/lib/api';
 *   const url = endpoints.contacts.fetchAll();
 */
// Consumed only through the barrel as `endpoints.receipts.*` (used by the
// receipts hooks + invoice detail). It has no direct named-import refs, so a
// "remove unused endpoint" sweep deleted it once (edaff35b7) and crashed the
// Receipts page + invoice detail. Keep it un-exported and barrel-only.
const receiptEndpoints = {
  list: () => '/receipts',
  detail: (id: string) => `/receipts/${id}`,
  pdf: (id: string) => `/receipts/${id}.pdf`,
  resend: (id: string) => `/receipts/${id}/resend`,
  forPayment: (paymentId: string) => `/payments/${paymentId}/receipt`,
  forDeposit: (depositId: string) => `/deposits/${depositId}/receipt`,
};

export const notificationEndpoints = {
  list: () => '/notifications',
  groupItems: (id: string) => `/notifications/${id}/group-items`,
  unreadCount: () => '/notifications/unread-count',
  markRead: (id: string) => `/notifications/${id}/read`,
  archive: (id: string) => `/notifications/${id}/archive`,
  readAll: () => '/notifications/read-all',
  deviceTokens: () => '/notifications/device-tokens',
  deviceToken: (id: string) => `/notifications/device-tokens/${id}`,
  broadcast: () => '/notifications',
} as const;

export const endpoints = {
  auth: authEndpoints,
  contacts: contactEndpoints,
  contactStatuses: contactStatusEndpoints,
  leads: leadEndpoints,
  invoices: invoiceEndpoints,
  receipts: receiptEndpoints,
  notes: noteEndpoints,
  changeOrders: changeOrderEndpoints,
  settings: settingsEndpoints,
  timesheets: timesheetEndpoints,
  appointmentForms: appointmentFormEndpoints,
  tasks: taskEndpoints,
  scheduler: schedulerEndpoints,
  promotions: promotionsEndpoints,
  auditLogs: auditLogEndpoints,
  opsEstimates: opsEstimateEndpoints,
  entityTypes: entityTypeEndpoints,
  tags: tagEndpoints,
  opsProposals: opsProposalEndpoints,
  estimateTemplates: estimateTemplateEndpoints,
  proposalTemplates: proposalTemplateEndpoints,
  jobs: jobEndpoints,
  recurringBilling: recurringBillingEndpoints,
  officeRecoveryRenewal: officeRecoveryRenewalEndpoints,
  jobPipelines: jobPipelineEndpoints,
  appointmentAuth: appointmentAuthEndpoints,
  appointments: appointmentEndpoints,
  paymentSchedules: paymentScheduleEndpoints,
  consents: consentEndpoints,
  expenses: expenseEndpoints,
  taxZones: taxZoneEndpoints,
  taxGroups: taxGroupEndpoints,
  taxPolicies: taxPolicyEndpoints,
  taxCodes: taxCodeEndpoints,
  serviceAreas: serviceAreaEndpoints,
  onboarding: onboardingEndpoints,
  customFields: customFieldEndpoints,
  creditMemos: creditMemoEndpoints,
  reminderPoliciesV2,
  remindersV2,
  reviewsV2: reviewsV2Endpoints,
  reviewPlatformsV2: reviewPlatformsV2Endpoints,
  webhookSubscriptions: webhookSubscriptionEndpoints,
  zapier: zapierEndpoints,
  billingV2: billingV2Endpoints,
} as const;

// ─── Public (unauthenticated) endpoints ───────────────────────────────────────

export const publicBookingEndpoints = {
  linkConfig: (key: string) => `/api/public/booking-links/${key}`,
  availability: (key: string, serviceId: string) =>
    `/api/public/booking-links/${key}/services/${serviceId}/availability`,
  /** Itemized pricebook quote (with tax) for the customer's address. */
  quote: (key: string, serviceId: string) =>
    `/api/public/booking-links/${key}/services/${serviceId}/quote`,
  createBooking: (key: string) => `/api/public/booking-links/${key}/bookings`,
} as const;

export const publicPaymentEndpoints = {
  paymentPage: (paymentId: string) => `/api/public/payment/${paymentId}`,
  createPaymentIntent: () => '/api/public/payment/create-intent',
  payabliCharge: () => '/api/public/payment/payabli-charge',
  tipPage: (id: string) => `/api/public/tip/${id}`,
  createTipSession: (id: string) => `/api/public/tip/${id}/stripe-session`,
  tipPayabliCharge: (id: string) => `/api/public/tip/${id}/payabli-charge`,
  signedPage: (signedId: string) => `/api/public/signed/${signedId}`,
} as const;

export const publicContactPaymentMethodRequestEndpoints = {
  detail: (token: string) => `/contacts/payment-method-requests/${token}`,
  setupIntent: (token: string) => `/contacts/payment-method-requests/${token}/setup-intent`,
  complete: (token: string) => `/contacts/payment-method-requests/${token}/complete`,
  tokenize: (token: string) => `/contacts/payment-method-requests/${token}/tokenize`,
} as const;

// ─── Public phone-link confirmation (prove you own the email) ────────────────

export const publicContactPhoneLinkEndpoints = {
  detail: (token: string) => `/contacts/phone-link/${token}`,
  confirm: (token: string) => `/contacts/phone-link/${token}/confirm`,
} as const;

// ─── Public Click-to-Confirm (spec §6) ────────────────────────────────────────

export const publicConfirm = {
  landing: (token: string) => `/api/public/confirm/${token}`,
  confirm: (token: string) => `/api/public/confirm/${token}/confirm`,
  reschedule: (token: string) => `/api/public/confirm/${token}/reschedule`,
  cancel: (token: string) => `/api/public/confirm/${token}/cancel`,
} as const;

export const publicEmergencyAck = {
  landing: (token: string) => `/api/public/emergency-ack/${token}`,
  acknowledge: (token: string) => `/api/public/emergency-ack/${token}/acknowledge`,
} as const;

// ─── Public Reviews (spec §7) ─────────────────────────────────────────────────

export const publicReviews = {
  landing: (token: string) => `/v2/public/reviews/landing/${token}`,
  clickPlatform: (token: string) => `/v2/public/reviews/${token}/click-platform`,
  submit: (token: string) => `/v2/public/reviews/${token}/submit`,
} as const;

export const publicJobStories = {
  landing: (token: string) => `/v2/public/job-stories/${encodeURIComponent(token)}`,
} as const;

// ─── Deposits (Wave 2 — spec §4 / plan PR 3) ──────────────────────────────────
//
// Mounted at `/deposits/*`; the `/v2/billing/deposits/*` mirror is
// served by the same module and is the preferred path for new clients.
export const depositEndpoints = {
  list: () => '/deposits',
  detail: (id: string) => `/deposits/${id}`,
  capture: () => '/deposits',
  apply: (id: string) => `/deposits/${id}/apply`,
  refund: (id: string) => `/deposits/${id}/refund`,
  forfeit: (id: string) => `/deposits/${id}/forfeit`,
  contactBalance: (contactId: string) => `/contacts/${contactId}/deposits/balance`,
} as const;

// ─── QBO Reconciliation + Dead-Letter (spec §9.6 / §9.7 — PR 8) ────────────────

export const qboReconciliationEndpoints = {
  latestReport: () => '/qbo/reconciliation',
  reportById: (reportId: string) => `/qbo/reconciliation/${reportId}`,
  runManual: () => '/qbo/reconciliation/run',
  resolveMismatch: (reportId: string, mismatchId: string) =>
    `/qbo/reconciliation/${reportId}/resolve/${mismatchId}`,
  deadLetter: () => '/qbo/dead-letter',
  resyncEntity: (entity: string, id: string) => `/qbo/sync/${entity}/${id}`,
} as const;

// ─── Job Reports (spec §12) ───────────────────────────────────────────────────

export const jobReportEndpoints = {
  list: () => '/reports',
  detail: (id: string) => `/reports/${id}`,
  create: () => '/reports',
  update: (id: string) => `/reports/${id}`,
  // Sections (doc-editor redo plan §2.1) — section bodies are TipTap docs.
  sections: (id: string) => `/reports/${id}/sections`,
  section: (id: string, sid: string) => `/reports/${id}/sections/${sid}`,
  sectionsReorder: (id: string) => `/reports/${id}/sections/reorder`,
  submitForReview: (id: string) => `/reports/${id}/submit-for-review`,
  approve: (id: string) => `/reports/${id}/approve`,
  sendBack: (id: string) => `/reports/${id}/send-back`,
  publish: (id: string) => `/reports/${id}/publish`,
  // Spec §3.4 — open / abandon a working revision on a published report.
  revise: (id: string) => `/reports/${id}/revise`,
  discardRevision: (id: string) => `/reports/${id}/discard-revision`,
  unsign: (id: string) => `/reports/${id}/unsign`,
  archive: (id: string) => `/reports/${id}/archive`,
  revisions: (id: string) => `/reports/${id}/revisions`,
  // Merge fields: the catalog (no values, for template editing) and the
  // values a given report resolves to.
  fieldCatalog: () => '/report-fields',
  fields: (id: string) => `/reports/${id}/fields`,
  share: (id: string) => `/reports/${id}/share`,
  shareInfo: (id: string) => `/reports/${id}/share/info`,
  accessLog: (id: string) => `/reports/${id}/access-log`,
  // Photos
  uploadUrl: (id: string) => `/reports/${id}/photos/upload-url`,
  photoConfirm: (id: string) => `/reports/${id}/photos`,
  photos: (id: string) => `/reports/${id}/photos`,
  photoDetail: (id: string, pid: string) => `/reports/${id}/photos/${pid}`,
  // AI
  aiTranscribe: (id: string) => `/reports/${id}/ai/transcribe`,
  aiSuggestSummary: (id: string) => `/reports/${id}/ai/suggest-summary`,
  aiSuggestRecommendations: (id: string) => `/reports/${id}/ai/suggest-recommendations`,
  // Job-scoped (Appendix A)
  forJob: (jobId: string) => `/jobs/${jobId}/reports`,
  setJobPrimary: (jobId: string) => `/jobs/${jobId}/primary-report`,
} as const;

// Analytics reports (domain dashboards) — distinct from the documental
// `jobReportEndpoints` above.
export const jobAnalyticsReportEndpoints = {
  salesReport: (params?: ReportPeriodParams) =>
    `/reports/jobs/sales-report${reportPeriodQuery(params)}`,
  hotspots: (params?: JobHotspotReportParams) => {
    const search = new URLSearchParams(reportPeriodQuery(params).replace(/^\?/, ''));
    if (params?.jobTypeId) search.set('jobTypeId', params.jobTypeId);
    if (params?.lowVolumeThreshold != null) {
      search.set('lowVolumeThreshold', String(params.lowVolumeThreshold));
    }
    const query = search.toString();
    return `/reports/jobs/hotspots${query ? `?${query}` : ''}`;
  },
} as const;

export const leadAnalyticsReportEndpoints = {
  overview: (params?: ReportPeriodParams) => `/reports/leads/overview${reportPeriodQuery(params)}`,
} as const;

export const arReportEndpoints = {
  summary: (params?: ReportPeriodParams) => `/reports/ar/summary${reportPeriodQuery(params)}`,
} as const;

// NOT-542 — Divisional P&L by Business Unit.
export const pnlReportEndpoints = {
  byBusinessUnit: (params?: ReportPeriodParams) =>
    `/reports/pnl-by-business-unit${reportPeriodQuery(params)}`,
} as const;

export const technicianAnalyticsReportEndpoints = {
  performance: (params?: ReportPeriodParams) =>
    `/reports/technicians/performance${reportPeriodQuery(params)}`,
} as const;

export const customerReportEndpoints = {
  summary: (params?: ReportPeriodParams) =>
    `/reports/customers/summary${reportPeriodQuery(params)}`,
} as const;

export const executiveReportEndpoints = {
  summary: (params?: ReportPeriodParams) =>
    `/reports/executive/summary${reportPeriodQuery(params)}`,
} as const;

export const operationalInsightEndpoints = {
  dayZero: () => '/reports/operational/day-zero',
  homeOverview: (params?: ReportPeriodParams) =>
    `/reports/operational/home-overview${reportPeriodQuery(params)}`,
  operationsDashboard: (params?: ReportPeriodParams) =>
    `/reports/operational/operations-dashboard${reportPeriodQuery(params)}`,
  officeScorecards: (params?: ReportPeriodParams) =>
    `/reports/operational/office-scorecards${reportPeriodQuery(params)}`,
  salesScorecards: (params?: ReportPeriodParams) =>
    `/reports/operational/sales-scorecards${reportPeriodQuery(params)}`,
  financeQueues: () => '/reports/operational/finance-queues',
  automationFailures: () => '/reports/operational/automation-failures',
  scheduling: (params?: ReportPeriodParams) =>
    `/reports/operational/scheduling${reportPeriodQuery(params)}`,
  serviceCalls: (params?: ReportPeriodParams) =>
    `/reports/operational/service-calls${reportPeriodQuery(params)}`,
  renewals: (params?: ReportPeriodParams) =>
    `/reports/operational/renewals${reportPeriodQuery(params)}`,
  procurement: () => '/part-requirements/overview',
} as const;

export interface TargetScopeParams {
  metric?: string;
  periodStart?: string;
  reference?: string;
  scope?: string;
  scopeId?: string;
  periodType?: string;
}

function targetQuery(params?: TargetScopeParams): string {
  const search = new URLSearchParams();
  if (params?.metric) search.set('metric', params.metric);
  if (params?.periodStart) search.set('periodStart', params.periodStart);
  if (params?.reference) search.set('reference', params.reference);
  if (params?.scope) search.set('scope', params.scope);
  if (params?.scopeId) search.set('scopeId', params.scopeId);
  if (params?.periodType) search.set('periodType', params.periodType);
  const q = search.toString();
  return q ? `?${q}` : '';
}

export const targetEndpoints = {
  metrics: () => '/report-targets/metrics',
  people: () => '/report-targets/people',
  list: (params?: TargetScopeParams) => `/report-targets${targetQuery(params)}`,
  attainment: (params?: TargetScopeParams) => `/report-targets/attainment${targetQuery(params)}`,
  suggestion: (params: TargetScopeParams) => `/report-targets/suggestion${targetQuery(params)}`,
  create: () => '/report-targets',
  remove: (id: string, effectivePeriod?: string) =>
    `/report-targets/${id}${effectivePeriod ? `?effectivePeriod=${encodeURIComponent(effectivePeriod)}` : ''}`,
} as const;

export const reportTemplateEndpoints = {
  list: () => '/report-templates',
  detail: (id: string) => `/report-templates/${id}`,
  create: () => '/report-templates',
  update: (id: string) => `/report-templates/${id}`,
  publish: (id: string) => `/report-templates/${id}/publish`,
  archive: (id: string) => `/report-templates/${id}/archive`,
  clone: (id: string) => `/report-templates/${id}/clone`,
  sections: (id: string) => `/report-templates/${id}/sections`,
  section: (id: string, sid: string) => `/report-templates/${id}/sections/${sid}`,
  reorderSections: (id: string) => `/report-templates/${id}/sections/reorder`,
} as const;

export const reportSettingsEndpoints = {
  all: () => '/settings/reports',
  tab: (tab: string) => `/settings/reports/${tab}`,
} as const;

export const contractSettingsEndpoints = {
  all: () => '/settings/contracts',
  tab: (tab: string) => `/settings/contracts/${tab}`,
} as const;

export const escalationRuleEndpoints = {
  list: () => '/escalation-rules',
  detail: (id: string) => `/escalation-rules/${id}`,
} as const;

export const publicReportEndpoints = {
  hostedView: (token: string) => `/public/reports/${token}`,
  pdf: (token: string) => `/public/reports/${token}/pdf`,
  accessLog: (token: string) => `/public/reports/${token}/access-log`,
  sign: (token: string) => `/public/reports/${token}/sign`,
  combinedDoc: (token: string) => `/public/combined-doc/${token}`,
} as const;

// ─── Payment Schedule Templates (spec §9) ────────────────────────────────────

export const paymentScheduleTemplateEndpoints = {
  forEstimate: (estimateId: string) => `/estimates/${estimateId}/schedule-template`,
  /**
   * `audience` decides whether the tenant's `showPaymentPlanPreview` setting
   * applies. It is customer-facing copy, so anything that ends up in front of
   * the customer (the PDF, the signed document) must ask as the CUSTOMER and
   * omit this. Only internal screens pass `staff`.
   */
  preview: (estimateId: string, audience?: 'staff') =>
    `/estimates/${estimateId}/schedule-template/preview${audience ? `?audience=${audience}` : ''}`,
} as const;

export const paymentSchedulePresetEndpoints = {
  list: () => '/schedule-presets',
  detail: (id: string) => `/schedule-presets/${id}`,
  create: () => '/schedule-presets',
  delete: (id: string) => `/schedule-presets/${id}`,
} as const;

// ─── Job schedule requests ("book a time" links) ──────────────────────────────

/** Office-facing (authenticated) endpoints. */
export const scheduleRequestEndpoints = {
  create: () => '/scheduler/schedule-requests',
  listForJob: (jobId: string) => `/scheduler/jobs/${jobId}/schedule-requests`,
  resend: (id: string) => `/scheduler/schedule-requests/${id}/resend`,
  revoke: (id: string) => `/scheduler/schedule-requests/${id}/revoke`,
} as const;

/** Customer-facing (public, token-scoped) endpoints. */
export const publicTrackingEndpoints = {
  // §9.4 — customer live-tracking data (operations-served, opaque token). The
  // shareable page the customer opens is the SPA route `/t/:token`; these are
  // the JSON calls that page makes. They live under `/api/public/*` (not the
  // page's `/t/*`) so the CloudFront SPA-router routes them straight to the
  // backend instead of racing the Accept-header rewrite that made the fetch
  // intermittently 404 as "link expired" in WhatsApp / iOS Safari. The namespace
  // is `live-tracking`, not `tracking`, so it never overlaps the legacy job/refno
  // endpoint `GET /api/public/tracking/:id?refno=...` (which the `/tracking/:id/
  // :refno` page still uses with appointment-id semantics).
  page: (token: string) => `/api/public/live-tracking/${token}`,
  data: (token: string) => `/api/public/live-tracking/${token}/data`,
  message: (token: string) => `/api/public/live-tracking/${token}/message`,
} as const;

// NOT-339 §13 — token-gated customer portal for recurring services.
export const recurringPortalEndpoints = {
  context: (token: string) => `/api/public/recurring-portal/${token}`,
  visits: (token: string) => `/api/public/recurring-portal/${token}/visits`,
  skip: (token: string, appointmentId: string) =>
    `/api/public/recurring-portal/${token}/visits/${appointmentId}/skip`,
  reschedule: (token: string, appointmentId: string) =>
    `/api/public/recurring-portal/${token}/visits/${appointmentId}/reschedule`,
  pause: (token: string, jobId: string) =>
    `/api/public/recurring-portal/${token}/services/${jobId}/pause`,
  cancel: (token: string, jobId: string) =>
    `/api/public/recurring-portal/${token}/services/${jobId}/cancel`,
  setAutoRenew: (token: string, jobId: string) =>
    `/api/public/recurring-portal/${token}/services/${jobId}/auto-renew`,
  // §8.5 — renewal re-authorization (consent re-validated).
  renewalAuthorization: (token: string, jobId: string) =>
    `/api/public/recurring-portal/${token}/services/${jobId}/renewal-authorization`,
  // §13 (M8) — customer payment-method management.
  paymentMethods: (token: string) => `/api/public/recurring-portal/${token}/payment-methods`,
  paymentMethodSetupIntent: (token: string) =>
    `/api/public/recurring-portal/${token}/payment-methods/setup-intent`,
  // Payabli analog of the SetupIntent — the embedded component's temp token.
  paymentMethodTokenize: (token: string) =>
    `/api/public/recurring-portal/${token}/payment-methods/tokenize`,
  removePaymentMethod: (token: string, pmId: string) =>
    `/api/public/recurring-portal/${token}/payment-methods/${pmId}`,
  reviewPreferences: (token: string) => `/api/public/recurring-portal/${token}/review-preferences`,
} as const;

export const publicScheduleRequestEndpoints = {
  context: (token: string) => `/api/public/schedule-requests/${token}`,
  availability: (token: string) => `/api/public/schedule-requests/${token}/availability`,
  book: (token: string) => `/api/public/schedule-requests/${token}/book`,
  confirm: (token: string) => `/api/public/schedule-requests/${token}/confirm`,
  reschedule: (token: string) => `/api/public/schedule-requests/${token}/reschedule`,
  requestReschedule: (token: string) => `/api/public/schedule-requests/${token}/request-reschedule`,
  cancel: (token: string) => `/api/public/schedule-requests/${token}/cancel`,
} as const;

// ─── Scheduling v2 — settings surface ────────────────────────────────────────
// docs/scheduling/scheduling-v2-architecture.md §4.1/§4.2, §5.4. These routes
// only exist in v2 and are NOT gated by the v1/v2 scheduling switch: the
// policy and arrival-window presets are a settings surface regardless of
// which booking API the board is using.
export const schedulingV2Endpoints = {
  policy: () => '/v2/scheduling/policy',
  bookingPolicy: () => '/v2/scheduling/booking-policy',
  arrivalWindows: () => '/v2/scheduling/arrival-windows',
  effectiveArrivalWindows: () => '/v2/scheduling/arrival-windows/effective',
  arrivalWindow: (id: string) => `/v2/scheduling/arrival-windows/${id}`,
  arrivalWindowsReorder: () => '/v2/scheduling/arrival-windows/reorder',
  policyOverrides: () => '/v2/scheduling/policy/overrides',
  policyOverride: (jobTypeId: string) => `/v2/scheduling/policy/overrides/${jobTypeId}`,
} as const;
